/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePersistentState } from '../hooks/usePersistentState';
import {
  Building2, BarChart3, CreditCard, Plus, ShieldCheck,
  Trash2, Eye, EyeOff, AlertCircle, CheckCircle, XCircle, Users,
  Key, TrendingUp, ChevronDown, DollarSign, Settings as SettingsIcon,
  ToggleLeft, ToggleRight, Save, AlertTriangle,
  LayoutGrid, X, Package, Boxes, Banknote, Lock,
  Inbox, Globe, BookOpen, LifeBuoy, Clock, Calendar, Mail,
  List, Pencil, Search, ArrowUpDown, Activity, FileText,
  ArrowLeft, ChevronRight,
  ArrowUp, ArrowDown, HardDrive,
  Sparkles, Zap, Bot, Loader2,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { ConfirmDeleteDialog, type DeleteConfirmState } from '../ui/ConfirmDeleteDialog';
import { CustomSelect } from '../components/shared/CustomSelect';
import {
  Organization, OrgPlan, OrgStatus, User, ApiKey, Department,
  PLAN_LIMITS, APP_REGISTRY, APP_IDS, AppDefinition,
  NotificationType, UserRole,
  ModuleRequest, ModuleRequestStatus, ModuleMaturity, MASTER_ORG_ID,
  isOwnerRole, isPlatformRole,
  SubscriptionPlan, PlanLimit, DEFAULT_PLAN_IDS,
  ModuleQuotaConfig, PlatformFeature, PlanFeatureValue, PlatformSettings, UserStatus,
} from '../domain/entities';

import { getRepository, isRemoteMode } from '../config/repository';
import { PLAN_LABELS, PLAN_BADGE_COLORS } from '../core/planGating';
import { PLATFORM_MODULES } from '../core/appRegistry';

const cn = (...classes: (string | undefined | false)[]) => classes.filter(Boolean).join(' ');

const repo = getRepository();

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface OrgWithStats extends Organization {
  appCount: number;
  userCount:  number;
  apiKeyCount: number;
  daysSinceSignup: number;
  userLimitPct: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan badge
// ─────────────────────────────────────────────────────────────────────────────

function PlanBadge({ plan }: { plan: OrgPlan }) {
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest', PLAN_BADGE_COLORS[plan])}>
      {PLAN_LABELS[plan]}
    </span>
  );
}

function getOrgStatusMeta(status: OrgStatus) {
  const map: Record<OrgStatus, { label: string; cls: string; dot: string; Icon: React.FC<{ size?: number }> }> = {
    [OrgStatus.ACTIVE]: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', Icon: CheckCircle },
    [OrgStatus.PENDING_APPROVAL]: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400', Icon: Clock },
    [OrgStatus.PENDING_PAYMENT]: { label: 'Pending Payment', cls: 'bg-orange-100 text-orange-700', dot: 'bg-orange-400', Icon: Banknote },
    [OrgStatus.REJECTED]: { label: 'Rejected', cls: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500', Icon: XCircle },
    [OrgStatus.SUSPENDED]: { label: 'Suspended', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500', Icon: XCircle },
    [OrgStatus.TRIAL]: { label: 'Trial', cls: 'bg-sky-100 text-sky-700', dot: 'bg-sky-400', Icon: AlertCircle },
  };

  return map[status] ?? map[OrgStatus.PENDING_APPROVAL];
}

function StatusBadge({ status }: { status: OrgStatus }) {
  const { label, cls, Icon } = getOrgStatusMeta(status);
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest', cls)}>
      <Icon size={10} /> {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified Org Drawer — Create + Edit in a single side panel
// ─────────────────────────────────────────────────────────────────────────────

interface OrgDrawerProps {
  org?: OrgWithStats | null;
  onClose: () => void;
  onSave: (org: Organization, adminUser?: { email: string; password: string; name: string }) => void;
}

function OrgDrawer({ org, onClose, onSave }: OrgDrawerProps) {
  const isCreate = !org;
  const [name, setName] = useState(org?.name ?? '');
  const [slug, setSlug] = useState(org?.slug ?? '');
  const [industry, setIndustry] = useState(org?.industry ?? '');
  const [subscriptionPlanId, setSubscriptionPlanId] = useState(org?.subscriptionPlanId ?? '');
  const [status, setStatus] = useState<OrgStatus>(org?.status ?? OrgStatus.ACTIVE);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [manualUserApproval, setManualUserApproval] = useState(org?.manualUserApproval ?? false);
  const [aiEnabled, setAiEnabled] = useState(org?.aiEnabled !== false);
  const [maintenanceOverride, setMaintenanceOverride] = useState(org?.maintenanceOverride ?? false);
  const [provisionedRoles, setProvisionedRoles] = useState<UserRole[]>(
    org?.provisionedRoles ?? [UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER, UserRole.EMPLOYEE]
  );
  const [trialEndDate, setTrialEndDate] = useState(
    org?.trialEndDate ? new Date(org.trialEndDate).toISOString().split('T')[0] : ''
  );
  // Create-mode admin fields
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminName, setAdminName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Primary Owner (edit mode)
  const [primaryOwner, setPrimaryOwner] = useState<User | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetTempPassword, setResetTempPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  // Dirty-check discard protection
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    repo.getSubscriptionPlans().then(setPlans);
  }, []);

  // Fetch primary owner (edit mode)
  useEffect(() => {
    if (!org) return;
    repo.getUsersForOrg(org.id).then(users => {
      const admin = users.find(u => u.role === UserRole.ADMIN);
      setPrimaryOwner(admin ?? null);
    }).catch(() => {});
  }, [org]);

  // Dirty-check: compare current values to initial values
  const isDirty = (() => {
    if (isCreate) {
      return !!(name.trim() || adminEmail.trim() || adminPassword.trim() || adminName.trim() || industry.trim());
    }
    if (!org) return false;
    return name !== (org.name ?? '')
      || slug !== (org.slug ?? '')
      || industry !== (org.industry ?? '')
      || subscriptionPlanId !== (org.subscriptionPlanId ?? '')
      || status !== (org.status ?? OrgStatus.ACTIVE)
      || manualUserApproval !== (org.manualUserApproval ?? false)
      || aiEnabled !== (org.aiEnabled !== false)
      || maintenanceOverride !== (org.maintenanceOverride ?? false)
      || JSON.stringify(provisionedRoles) !== JSON.stringify(org.provisionedRoles ?? [UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER, UserRole.EMPLOYEE]);
  })();

  const guardedClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const derivedSlug = (n: string) => n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const PROVISIONABLE_ROLES: { value: UserRole; label: string }[] = [
    { value: UserRole.ADMIN, label: 'Admin' },
    { value: UserRole.DEPARTMENT_HEAD, label: 'Department Head' },
    { value: UserRole.MANAGER, label: 'Manager' },
    { value: UserRole.EMPLOYEE, label: 'Employee' },
  ];

  const toggleRole = (role: UserRole) => {
    setProvisionedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Organisation name is required.'); return; }
    if (isCreate) {
      if (!adminEmail.trim() || !adminEmail.includes('@')) { setError('A valid admin email is required.'); return; }
      if (!adminPassword.trim() || adminPassword.length < 6) { setError('Admin password must be at least 6 characters.'); return; }
      if (!adminName.trim()) { setError('Admin name is required.'); return; }
    }
    setSaving(true);

    const planMap: Record<string, OrgPlan> = {
      [DEFAULT_PLAN_IDS.FREE]: OrgPlan.FREE,
      [DEFAULT_PLAN_IDS.BASIC]: OrgPlan.BASIC,
      [DEFAULT_PLAN_IDS.PRO]: OrgPlan.PRO,
      [DEFAULT_PLAN_IDS.ENTERPRISE]: OrgPlan.ENTERPRISE,
    };

    const finalSlug = slug.trim() || derivedSlug(name);
    const basePlan = org?.plan ?? OrgPlan.BASIC;
    const derivedPlan = subscriptionPlanId ? (planMap[subscriptionPlanId] ?? basePlan) : basePlan;
    const now = Date.now();

    const orgData: Organization = {
      id: org?.id ?? crypto.randomUUID(),
      name: name.trim(),
      slug: finalSlug,
      industry: industry.trim() || undefined,
      status,
      plan: derivedPlan,
      createdAt: org?.createdAt ?? now,
      updatedAt: now,
      trialStartDate: status === OrgStatus.TRIAL ? (org?.trialStartDate ?? now) : org?.trialStartDate,
      trialEndDate: trialEndDate ? new Date(trialEndDate).getTime() : org?.trialEndDate,
      enabledApps: org?.enabledApps ?? [],
      provisionedRoles,
      subscriptionPlanId: subscriptionPlanId || undefined,
      manualUserApproval,
      aiEnabled,
      maintenanceOverride,
    };

    if (isCreate) {
      await onSave(orgData, { email: adminEmail.trim(), password: adminPassword, name: adminName.trim() });
    } else {
      await onSave(orgData);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[150] flex justify-end" onClick={guardedClose}>
      <div className="absolute inset-0 bg-black/30 transition-opacity" />
      <div
        className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Discard confirmation dialog */}
        {showDiscardConfirm && (
          <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center p-6" onClick={e => e.stopPropagation()}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-xs space-y-4">
              <div className="flex items-center gap-2 text-amber-600">
                <AlertTriangle size={18} />
                <h3 className="text-sm font-black">Unsaved Changes</h3>
              </div>
              <p className="text-sm text-zinc-600">You have unsaved changes. Are you sure you want to discard them?</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDiscardConfirm(false)}
                  className="flex-1 px-4 py-2 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50 transition-colors"
                >
                  Keep Editing
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDiscardConfirm(false); onClose(); }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-colors"
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-200 bg-zinc-50 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-black text-zinc-900">{isCreate ? 'New Organisation' : 'Edit Organisation'}</h2>
            <p className="text-xs text-zinc-400 font-mono">{isCreate ? 'Create a new tenant on the platform' : org?.slug}</p>
          </div>
          <button onClick={guardedClose} className="p-2 rounded-lg hover:bg-zinc-200 transition-colors">
            <X size={18} className="text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2.5 flex items-center gap-2"><AlertCircle size={14} /> {error}</p>}

          {/* ─── Organisation Details ─── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest">
              <Building2 size={13} /> Organisation Details
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Organisation Name *</label>
              <input
                value={name}
                onChange={e => { setName(e.target.value); if (isCreate) setSlug(derivedSlug(e.target.value)); }}
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Tristar Logistics"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Slug</label>
              <input
                value={slug}
                onChange={e => setSlug(e.target.value)}
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                placeholder="tristar-logistics"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Industry</label>
              <input
                value={industry}
                onChange={e => setIndustry(e.target.value)}
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Restaurant Operations"
              />
            </div>
          </div>

          {/* ─── Admin User (create only) ─── */}
          {isCreate && (
            <div className="space-y-4 border-t border-zinc-100 pt-5">
              <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest">
                <Users size={13} /> Admin Account
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Admin Name *</label>
                <input
                  value={adminName}
                  onChange={e => setAdminName(e.target.value)}
                  className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Admin Email *</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="email"
                    value={adminEmail}
                    onChange={e => setAdminEmail(e.target.value)}
                    className="w-full rounded-xl border border-zinc-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="admin@company.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Admin Password *</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={adminPassword}
                    onChange={e => setAdminPassword(e.target.value)}
                    className="w-full rounded-xl border border-zinc-200 pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Min 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ─── Plan & Status ─── */}
          <div className="space-y-4 border-t border-zinc-100 pt-5">
            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest">
              <CreditCard size={13} /> Plan & Status
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Subscription Plan</label>
              <CustomSelect
                value={subscriptionPlanId}
                onChange={v => setSubscriptionPlanId(v)}
                options={plans.map(p => ({ value: p.id, label: `${p.name}${p.price > 0 ? ` — $${p.price}/mo` : ''}` }))}
                placeholder="Select plan…"
              />
              {subscriptionPlanId && (() => {
                const sp = plans.find(p => p.id === subscriptionPlanId);
                if (!sp) return null;
                return (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className={cn('px-2 py-0.5 rounded-full text-[9px] font-bold uppercase', sp.features.apiAccess ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400')}>
                      API {sp.features.apiAccess ? 'Enabled' : 'Disabled'}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-zinc-100 text-zinc-500">
                      {(sp.features.usageLimits.max_users ?? 0) === Infinity ? '∞' : (sp.features.usageLimits.max_users ?? 0)} Users
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-zinc-100 text-zinc-500">
                      {(sp.features.usageLimits.max_hubs ?? 0) === Infinity ? '∞' : (sp.features.usageLimits.max_hubs ?? 0)} Hubs
                    </span>
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Status</label>
              <CustomSelect
                value={status}
                onChange={v => setStatus(v as OrgStatus)}
                options={[
                  { value: OrgStatus.ACTIVE, label: 'Active' },
                  { value: OrgStatus.PENDING_APPROVAL, label: 'Pending Approval' },
                  { value: OrgStatus.PENDING_PAYMENT, label: 'Pending Payment' },
                  { value: OrgStatus.REJECTED, label: 'Rejected' },
                  { value: OrgStatus.SUSPENDED, label: 'Suspended' },
                  { value: OrgStatus.TRIAL, label: 'Trial' },
                ]}
                placeholder="Select status…"
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-zinc-200 px-4 py-3 bg-zinc-50">
              <div>
                <p className="text-sm font-bold text-zinc-900">Manual user approval</p>
                <p className="text-[11px] text-zinc-400">New tenant users stay pending until an org admin activates them.</p>
              </div>
              <button type="button" onClick={() => setManualUserApproval(v => !v)}>
                {manualUserApproval
                  ? <ToggleRight size={30} className="text-emerald-500" />
                  : <ToggleLeft size={30} className="text-zinc-300" />}
              </button>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-zinc-200 px-4 py-3 bg-zinc-50">
              <div>
                <p className="text-sm font-bold text-zinc-900">Allow AI Access</p>
                <p className="text-[11px] text-zinc-400">Enable AI translation features (✨ magic wand) for this organisation.</p>
              </div>
              <button type="button" onClick={() => setAiEnabled(v => !v)}>
                {aiEnabled
                  ? <ToggleRight size={30} className="text-emerald-500" />
                  : <ToggleLeft size={30} className="text-zinc-300" />}
              </button>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-amber-200 px-4 py-3 bg-amber-50/50">
              <div>
                <p className="text-sm font-bold text-zinc-900">Override: Enable Maintenance Center</p>
                <p className="text-[11px] text-zinc-400">Grant backup & recovery tools even if the plan doesn't include it.</p>
              </div>
              <button type="button" onClick={() => setMaintenanceOverride(v => !v)}>
                {maintenanceOverride
                  ? <ToggleRight size={30} className="text-amber-500" />
                  : <ToggleLeft size={30} className="text-zinc-300" />}
              </button>
            </div>
            {status === OrgStatus.TRIAL && (
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Trial End Date</label>
                <div className="relative">
                  <Calendar size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="date"
                    value={trialEndDate}
                    onChange={e => setTrialEndDate(e.target.value)}
                    className="w-full rounded-xl border border-zinc-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ─── Role Provisioning ─── */}
          <div className="space-y-4 border-t border-zinc-100 pt-5">
            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest">
              <ShieldCheck size={13} /> Role Provisioning
            </div>
            <p className="text-[11px] text-zinc-400">Select which roles are available for this organisation. Removing a role will flag affected users as restricted.</p>
            <div className="flex flex-wrap gap-2">
              {PROVISIONABLE_ROLES.map(({ value, label }) => {
                const checked = provisionedRoles.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleRole(value)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all',
                      checked
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400',
                    )}
                  >
                    <div className={cn(
                      'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all',
                      checked ? 'bg-white border-white' : 'border-zinc-300',
                    )}>
                      {checked && <CheckCircle size={10} className="text-indigo-600" />}
                    </div>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ─── Primary Owner (edit mode) ─── */}
          {!isCreate && org && primaryOwner && (
            <div className="space-y-3 border-t border-zinc-100 pt-5">
              <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest">
                <Key size={13} /> Primary Owner
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Name</span>
                  <span className="font-bold text-zinc-900">{primaryOwner.name}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Email</span>
                  <span className="font-bold text-zinc-900 text-xs font-mono">{primaryOwner.email}</span>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Reset Password</label>
                  <div className="relative">
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      value={resetTempPassword}
                      onChange={e => setResetTempPassword(e.target.value)}
                      placeholder="Enter new temporary password"
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-900 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(p => !p)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    >
                      {showResetPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={resettingPassword || !resetTempPassword.trim()}
                  onClick={async () => {
                    const pwd = resetTempPassword.trim();
                    if (pwd.length < 8 || !/\d/.test(pwd)) {
                      setError('Password must be at least 8 characters with at least 1 number.');
                      return;
                    }
                    setResettingPassword(true);
                    try {
                      await repo.saveUser({ ...primaryOwner, mustChangePassword: true, passwordHash: pwd });
                      setError('');
                      setResetTempPassword('');
                      setPrimaryOwner(prev => prev ? { ...prev, mustChangePassword: true } : prev);
                    } finally {
                      setResettingPassword(false);
                    }
                  }}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-colors',
                    (resettingPassword || !resetTempPassword.trim()) ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed border-zinc-200' : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100',
                  )}
                >
                  <Key size={12} /> {resettingPassword ? 'Resetting…' : 'Reset Password'}
                </button>
                {primaryOwner.mustChangePassword && (
                  <p className="text-[10px] text-amber-600 font-semibold flex items-center gap-1"><AlertTriangle size={10} /> User must change password on next login</p>
                )}
              </div>
            </div>
          )}

          {/* ─── Info Footer (edit mode) ─── */}
          {!isCreate && org && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-500">Created</span>
                <span className="font-bold text-zinc-900">{new Date(org.createdAt).toLocaleDateString()}</span>
              </div>
              {org.updatedAt && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Last Updated</span>
                  <span className="font-bold text-zinc-900">{new Date(org.updatedAt).toLocaleDateString()}</span>
                </div>
              )}
              {org.trialStartDate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Trial Started</span>
                  <span className="font-bold text-zinc-900">{new Date(org.trialStartDate).toLocaleDateString()}</span>
                </div>
              )}
              {org.trialEndDate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Trial Ends</span>
                  <span className={cn('font-bold', org.trialEndDate < Date.now() ? 'text-red-600' : 'text-zinc-900')}>
                    {new Date(org.trialEndDate).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-500">Users</span>
                <span className="font-bold text-zinc-900">{org.userCount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-500">Apps Installed</span>
                <span className="font-bold text-zinc-900">{org.appCount}</span>
              </div>
            </div>
          )}

          {/* ─── Action Buttons ─── */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={guardedClose} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors',
                saving || !name.trim() ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed' : 'bg-zinc-900 text-white hover:bg-zinc-800',
              )}
            >
              <Save size={14} /> {isCreate ? 'Create Organisation' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// App icon helper
// ─────────────────────────────────────────────────────────────────────────────

function AppIcon({ iconKey, size = 18 }: { iconKey: string; size?: number }) {
  const icons: Record<string, React.FC<{ size?: number; className?: string }>> = {
    Package, BarChart3, Boxes, Banknote, Globe, BookOpen, LifeBuoy,
  };
  const Icon = icons[iconKey] ?? Package;
  return <Icon size={size} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Manage Apps Modal
// ─────────────────────────────────────────────────────────────────────────────

function ManageAppsModal({ org, onClose, onSaved }: {
  org: Organization;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [enabled, setEnabled] = useState<string[]>(
    org.enabledApps ?? []
  );
  const [saving, setSaving] = useState(false);

  const toggle = (id: string) =>
    setEnabled(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const handleSave = async () => {
    setSaving(true);
    await repo.saveOrganization({ ...org, enabledApps: enabled });
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-100">
          <div>
            <h2 className="text-lg font-bold text-zinc-900">Manage Apps</h2>
            <p className="text-sm text-zinc-500 mt-0.5">{org.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* App list */}
        <div className="p-6 space-y-3">
          {APP_REGISTRY.map(app => {
            const isEnabled = enabled.includes(app.id);
            const isComingSoon = app.status === 'coming_soon';
            return (
              <div
                key={app.id}
                className={cn(
                  'flex items-center gap-4 p-4 rounded-xl border transition-all',
                  isComingSoon
                    ? 'border-zinc-100 bg-zinc-50 opacity-60'
                    : isEnabled
                      ? 'border-indigo-200 bg-indigo-50/50'
                      : 'border-zinc-200 bg-white'
                )}
              >
                <div className={cn(
                  'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
                  isEnabled && !isComingSoon ? 'bg-indigo-100 text-indigo-600' : 'bg-zinc-100 text-zinc-400'
                )}>
                  <AppIcon iconKey={app.iconKey} size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-zinc-900">{app.name}</span>
                    {isComingSoon && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-zinc-200 text-zinc-500">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5 truncate">{app.description}</p>
                </div>
                <button
                  onClick={() => !isComingSoon && toggle(app.id)}
                  disabled={isComingSoon}
                  title={isComingSoon ? 'Not yet available' : isEnabled ? 'Disable' : 'Enable'}
                  className="shrink-0 transition-colors"
                >
                  {isEnabled && !isComingSoon
                    ? <ToggleRight size={30} className="text-emerald-500" />
                    : <ToggleLeft size={30} className="text-zinc-300" />
                  }
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-zinc-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 disabled:opacity-60 transition-all"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Org Audit Modal — Module status + Launchpad Preview
// ─────────────────────────────────────────────────────────────────────────────

function OrgAuditModal({ org, onClose }: { org: Organization; onClose: () => void }) {
  const enabledApps: string[] = org.enabledApps ?? [];

  const getStatus = (app: AppDefinition) => {
    if (app.status === 'coming_soon') return { label: 'Unavailable', cls: 'bg-zinc-100 text-zinc-500' };
    return enabledApps.includes(app.id)
      ? { label: 'Enabled', cls: 'bg-emerald-100 text-emerald-700' }
      : { label: 'Disabled', cls: 'bg-red-100 text-red-600' };
  };

  const getReason = (app: AppDefinition) => {
    if (app.status === 'coming_soon') return 'Coming soon — not yet deployed on platform';
    return enabledApps.includes(app.id)
      ? 'Manually enabled by Super Admin'
      : 'Not provisioned for this organisation';
  };

  const visibleApps = APP_REGISTRY.filter(a => a.status === 'live' && enabledApps.includes(a.id));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-zinc-900">Module Audit</h2>
            <p className="text-sm text-zinc-500 mt-0.5">{org.name} — what they can see</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Module audit table */}
          <div>
            <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">All Platform Modules</h3>
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-100">
                    {['Module', 'Status', 'Reason'].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {APP_REGISTRY.map(app => {
                    const { label, cls } = getStatus(app);
                    return (
                      <tr key={app.id} className="hover:bg-zinc-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-500">
                              <AppIcon iconKey={app.iconKey} size={13} />
                            </div>
                            <span className="font-semibold text-zinc-900">{app.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest', cls)}>
                            {label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-500">{getReason(app)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Launchpad Preview */}
          <div>
            <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">Launchpad Preview</h3>
            {visibleApps.length === 0 ? (
              <div className="border border-dashed border-zinc-300 rounded-xl p-8 text-center text-zinc-400 text-sm italic">
                No live apps enabled — this org&apos;s launchpad will show no app cards.
              </div>
            ) : (
              <div className="flex gap-3 flex-wrap">
                {visibleApps.map(app => (
                  <div key={app.id} className="flex items-center gap-2.5 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
                      <AppIcon iconKey={app.iconKey} size={15} />
                    </div>
                    <span className="font-bold text-sm text-indigo-900">{app.name}</span>
                    <CheckCircle size={13} className="text-emerald-500" />
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-zinc-400 mt-2">
              Showing apps that will be visible in the org&apos;s sidebar and launchpad based on current enabledApps configuration.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-100 shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Organizations
// ─────────────────────────────────────────────────────────────────────────────

function OrganizationsTab({ onImpersonate, onInspect, canInspect, canDeleteOrg, canChangePlan }: {
  onImpersonate: (org: Organization) => void;
  onInspect: (org: Organization) => void;
  canInspect: boolean;
  canDeleteOrg?: boolean;
  canChangePlan?: boolean;
}) {
  const [orgs, setOrgs] = useState<OrgWithStats[]>([]);
  const [orgDrawer, setOrgDrawer] = useState<'new' | OrgWithStats | null>(null);
  const [manageAppsOrg, setManageAppsOrg] = useState<Organization | null>(null);
  const [auditOrg, setAuditOrg] = useState<Organization | null>(null);
  const [pendingByOrg, setPendingByOrg] = useState<Record<string, number>>({});
  const [drawerOrg, setDrawerOrg] = useState<OrgWithStats | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchQuery, setSearchQuery] = usePersistentState('search');
  const [typedDeleteConfirm, setTypedDeleteConfirm] = useState<{ org: OrgWithStats; typed: string } | null>(null);

  const load = useCallback(async () => {
    const [allOrgs, allRequests] = await Promise.all([
      repo.getOrganizations(),
      repo.getModuleRequests(),
    ]);

    let userCountByOrg: Record<string, number> = {};
    let apiKeyCountByOrg: Record<string, number> = {};

    const counts = await Promise.all(
      allOrgs.map(async (org) => [org.id, (await repo.getUsersForOrg(org.id)).length] as const),
    );
    userCountByOrg = Object.fromEntries(counts);

    // Build pending count map
    const pMap: Record<string, number> = {};
    for (const r of allRequests) {
      if (r.status === ModuleRequestStatus.PENDING) {
        pMap[r.orgId] = (pMap[r.orgId] || 0) + 1;
      }
    }
    setPendingByOrg(pMap);

    setOrgs(allOrgs.map(org => {
      const orgUsers = userCountByOrg[org.id] ?? 0;
      const maxUsers = PLAN_LIMITS[org.plan]?.usageLimits?.max_users ?? Infinity;
      const userLimitPct = maxUsers === Infinity ? 0 : Math.round((orgUsers / maxUsers) * 100);
      const daysSinceSignup = Math.floor((Date.now() - (org.createdAt || Date.now())) / 86400000);
      return {
        ...org,
        appCount:    (org.enabledApps ?? []).length,
        userCount:   orgUsers,
        apiKeyCount: apiKeyCountByOrg[org.id] ?? 0,
        daysSinceSignup,
        userLimitPct,
      };
    }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleStatus = async (org: Organization) => {
    const newStatus = org.status === OrgStatus.ACTIVE ? OrgStatus.SUSPENDED : OrgStatus.ACTIVE;
    const updated: Organization = { ...org, status: newStatus };
    await repo.saveOrganization(updated);

    // Scenario C: notify all org admins when org is suspended
    if (newStatus === OrgStatus.SUSPENDED) {
      const allUsers = await getRepository().getUsers();
      const orgAdmins = allUsers.filter(u => u.role === UserRole.ADMIN && u.orgId === org.id);
      for (const admin of orgAdmins) {
        await getRepository().saveNotification({
          id: crypto.randomUUID(),
          recipientId: admin.id,
          type: NotificationType.WARNING,
          title: 'Organisation Suspended',
          message: `${org.name} has been suspended by the platform administrator.`,
          targetPath: '/',
          isRead: false,
          createdAt: Date.now(),
          orgId: org.id,
        });
      }
    }

    await load();
  };

  const handleChangePlan = async (org: Organization, plan: OrgPlan) => {
    await repo.saveOrganization({ ...org, plan });
    await load();
  };

  const handleDelete = async (org: OrgWithStats) => {
    setTypedDeleteConfirm({ org, typed: '' });
  };

  const confirmTypedDelete = async () => {
    if (!typedDeleteConfirm || typedDeleteConfirm.typed !== 'DELETE') return;
    await repo.deleteOrganization(typedDeleteConfirm.org.id);
    setTypedDeleteConfirm(null);
    await load();
  };

  const handleSaveOrg = async (orgData: Organization, adminUser?: { email: string; password: string; name: string }) => {
    await repo.saveOrganization(orgData);

    // Create mode: also create admin user + default department
    if (adminUser) {
      const defaultDept: Department = {
        id: `dept-${orgData.id}-general`,
        name: 'General',
        orgId: orgData.id,
        order: 0,
        linkedModules: [],
      };
      await repo.saveDepartment(defaultDept);

      const newAdmin: User = {
        id: crypto.randomUUID(),
        name: adminUser.name,
        email: adminUser.email,
        role: UserRole.ADMIN,
        status: UserStatus.ACTIVE,
        assignedBrands: [],
        assignedBranches: [],
        assignedModules: [],
        orgId: orgData.id,
        passwordHash: adminUser.password,
        mustChangePassword: true,
      };
      await repo.saveUser(newAdmin);
    }

    // ── Cascade Access Restriction ──
    if (orgData.provisionedRoles) {
      const orgUsers = await repo.getUsersForOrg(orgData.id);
      const allowed = new Set(orgData.provisionedRoles);
      for (const u of orgUsers) {
        if (isPlatformRole(u.role)) continue;
        const wasRestricted = u.accessRestricted ?? false;
        const shouldRestrict = !allowed.has(u.role);
        if (shouldRestrict !== wasRestricted) {
          await repo.saveUser({ ...u, accessRestricted: shouldRestrict });
        }
      }
    }

    setOrgDrawer(null);
    await load();
  };

  // Filtered orgs
  const filteredOrgs = orgs.filter(o => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q);
  });

  return (
    <div>
      {orgDrawer !== null && (
        <OrgDrawer
          org={orgDrawer === 'new' ? null : orgDrawer}
          onClose={() => setOrgDrawer(null)}
          onSave={handleSaveOrg}
        />
      )}
      {manageAppsOrg && (
        <ManageAppsModal
          org={manageAppsOrg}
          onClose={() => setManageAppsOrg(null)}
          onSaved={load}
        />
      )}
      {auditOrg && (
        <OrgAuditModal org={auditOrg} onClose={() => setAuditOrg(null)} />
      )}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-zinc-900">Organisations</h3>
          <p className="text-sm text-zinc-500">{filteredOrgs.length} of {orgs.length} tenant{orgs.length !== 1 ? 's' : ''} on the platform</p>
        </div>
        {/* Search */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search orgs…"
            className="pl-9 pr-3 py-2 text-sm border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 w-56"
          />
        </div>
        {/* View toggle */}
        <div className="flex items-center bg-zinc-100 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('grid')}
            className={cn('p-2 rounded-md transition-colors', viewMode === 'grid' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-400 hover:text-zinc-600')}
            title="Grid View"
          >
            <LayoutGrid size={15} />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={cn('p-2 rounded-md transition-colors', viewMode === 'table' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-400 hover:text-zinc-600')}
            title="Table View"
          >
            <List size={15} />
          </button>
        </div>
        <button
          onClick={() => setOrgDrawer('new')}
          className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-all shrink-0"
        >
          <Plus size={15} /> New Organisation
        </button>
      </div>

      {filteredOrgs.length === 0 ? (
        <div className="py-16 text-center text-zinc-400 italic text-sm border border-dashed border-zinc-200 rounded-2xl">
          {searchQuery.trim() ? 'No organisations match your search.' : 'No organisations yet.'}
        </div>
      ) : viewMode === 'table' ? (
        /* ── Table View ──────────────────────────────────────────────── */
        <div className="border border-zinc-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-4 py-3 font-bold text-zinc-600 text-xs uppercase tracking-widest">Organisation</th>
                <th className="text-left px-4 py-3 font-bold text-zinc-600 text-xs uppercase tracking-widest">Plan</th>
                <th className="text-left px-4 py-3 font-bold text-zinc-600 text-xs uppercase tracking-widest">Status</th>
                <th className="text-center px-4 py-3 font-bold text-zinc-600 text-xs uppercase tracking-widest">Apps</th>
                <th className="text-center px-4 py-3 font-bold text-zinc-600 text-xs uppercase tracking-widest">Users</th>
                <th className="text-left px-4 py-3 font-bold text-zinc-600 text-xs uppercase tracking-widest">Created</th>
                <th className="text-right px-4 py-3 font-bold text-zinc-600 text-xs uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.map(org => {
                const hasConsent = !!org.supportAccessEnabled && (!org.supportAccessExpiry || Date.now() <= org.supportAccessExpiry);
                return (
                  <tr key={org.id} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'w-2 h-2 rounded-full shrink-0',
                          getOrgStatusMeta(org.status).dot,
                        )} />
                        <div className="min-w-0">
                          <p className="font-bold text-zinc-900 truncate cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => setDrawerOrg(org)}>{org.name}</p>
                          <p className="text-[11px] text-zinc-400 font-mono">{org.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><PlanBadge plan={org.plan} /></td>
                    <td className="px-4 py-3"><StatusBadge status={org.status} /></td>
                    <td className="px-4 py-3 text-center"><span className="font-bold text-zinc-700">{org.appCount}</span></td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn('font-bold', org.userLimitPct >= 80 ? 'text-amber-600' : 'text-zinc-700')}>{org.userCount}</span>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{new Date(org.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setOrgDrawer(org)} title="Edit" className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setManageAppsOrg(org)} title="Apps" className="p-1.5 rounded-lg hover:bg-violet-50 text-violet-400 hover:text-violet-600 transition-colors">
                          <LayoutGrid size={14} />
                        </button>
                        <button onClick={() => handleToggleStatus(org)} title={org.status === OrgStatus.ACTIVE ? 'Suspend' : 'Activate'} className={cn('p-1.5 rounded-lg transition-colors', org.status === OrgStatus.ACTIVE ? 'hover:bg-amber-50 text-amber-400 hover:text-amber-600' : 'hover:bg-emerald-50 text-emerald-400 hover:text-emerald-600')}>
                          {org.status === OrgStatus.ACTIVE ? <XCircle size={14} /> : <CheckCircle size={14} />}
                        </button>
                        {canDeleteOrg && (
                        <button onClick={() => handleDelete(org)} title="Delete" className="p-1.5 rounded-lg hover:bg-red-50 text-red-300 hover:text-red-600 transition-colors">
                          <Trash2 size={14} />
                        </button>
                        )}
                        {canInspect && (
                        <button onClick={() => onInspect(org)} title="Inspect" className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-400 hover:text-indigo-600 transition-colors">
                          <Eye size={14} />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredOrgs.map(org => {
            const enabledApps: string[] = org.enabledApps ?? [];
            const hasConsent = !!org.supportAccessEnabled && (!org.supportAccessExpiry || Date.now() <= org.supportAccessExpiry);
            return (
              <div key={org.id} className="bg-white border border-zinc-200 rounded-2xl shadow-sm flex flex-col overflow-hidden">

                {/* ── Card header ────────────────────────────────────── */}
                <div className="p-5 border-b border-zinc-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className={cn(
                          'w-2 h-2 rounded-full shrink-0',
                          getOrgStatusMeta(org.status).dot,
                        )} />
                        <h3 className="font-bold text-zinc-900 truncate hover:text-indigo-600 cursor-pointer transition-colors" onClick={() => setDrawerOrg(org)}>{org.name}</h3>
                      </div>
                      <p className="text-xs text-zinc-400 font-mono pl-4">
                        {org.slug} &middot; {new Date(org.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {/* Plan — hover to change (owner only) */}
                      <div className="relative group/plan">
                        <PlanBadge plan={org.plan} />
                        {canChangePlan && (
                        <div className="absolute right-0 top-full mt-1 hidden group-hover/plan:block bg-white border border-zinc-200 rounded-xl shadow-lg z-10 p-1 min-w-[110px]">
                          {Object.values(OrgPlan).map(p => (
                            <button
                              key={p}
                              onClick={() => handleChangePlan(org, p)}
                              className={cn('block w-full text-left px-3 py-1.5 text-xs rounded-lg hover:bg-zinc-50', p === org.plan && 'font-bold text-indigo-600')}
                            >
                              {PLAN_LABELS[p]}
                            </button>
                          ))}
                        </div>
                        )}
                      </div>
                      <StatusBadge status={org.status} />
                      {(pendingByOrg[org.id] ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-orange-100 text-orange-700 border border-orange-200 animate-pulse">
                          <Inbox size={10} /> {pendingByOrg[org.id]} Pending
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Module chips ────────────────────────────────────── */}
                <div className="px-5 py-3 border-b border-zinc-100">
                  <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-2">Modules</p>
                  <div className="flex flex-wrap gap-1.5">
                    {APP_REGISTRY.map(app => {
                      const isEnabled = enabledApps.includes(app.id);
                      const isLive    = app.status === 'live';
                      return (
                        <span
                          key={app.id}
                          className={cn(
                            'inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full',
                            isLive && isEnabled  ? 'bg-indigo-100 text-indigo-700' :
                            isLive && !isEnabled ? 'bg-zinc-100 text-zinc-400'     :
                                                   'bg-zinc-50 text-zinc-300',
                          )}
                        >
                          <AppIcon iconKey={app.iconKey} size={9} />
                          {app.name}
                          {isLive && isEnabled  && <CheckCircle size={8} />}
                          {isLive && !isEnabled && <XCircle size={8} />}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* ── Stats row ───────────────────────────────────────── */}
                <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-4 flex-wrap overflow-hidden">
                  <div className="flex items-center gap-1.5 text-sm text-zinc-600" title="Apps Installed">
                    <LayoutGrid size={13} className="text-zinc-400" />
                    <span className="font-bold">{org.appCount}</span>
                    <span className="text-xs text-zinc-400">apps</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-zinc-600" title="Users">
                    <Users size={13} className="text-zinc-400" />
                    <span className="font-bold">{org.userCount}</span>
                    <span className="text-xs text-zinc-400">users</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-zinc-600" title="Days Since Signup">
                    <Clock size={13} className="text-zinc-400" />
                    <span className="font-bold">{org.daysSinceSignup}d</span>
                    <span className="text-xs text-zinc-400">age</span>
                  </div>
                  {org.userLimitPct > 0 && (
                    <div className={cn("flex items-center gap-1.5 text-sm", org.userLimitPct >= 80 ? "text-amber-600" : "text-zinc-600")} title="User Limit Usage">
                      <TrendingUp size={13} className={org.userLimitPct >= 80 ? "text-amber-400" : "text-zinc-400"} />
                      <span className="font-bold">{org.userLimitPct}%</span>
                      <span className="text-xs text-zinc-400">limit</span>
                    </div>
                  )}
                </div>

                {/* ── Actions ─────────────────────────────────────────── */}
                <div className="p-4 mt-auto space-y-2.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Edit */}
                    <button
                      onClick={() => setOrgDrawer(org)}
                      title="Edit organisation"
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-zinc-50 hover:bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider transition-colors"
                    >
                      <Pencil size={11} /> Edit
                    </button>

                    {/* Manage apps */}
                    <button
                      onClick={() => setManageAppsOrg(org)}
                      title="Manage installed apps"
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-violet-50 hover:bg-violet-100 text-violet-600 text-[10px] font-bold uppercase tracking-wider transition-colors"
                    >
                      <LayoutGrid size={11} /> Apps
                    </button>

                    {/* Module audit */}
                    <button
                      onClick={() => setAuditOrg(org)}
                      title="Module Audit — what this org can see"
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[10px] font-bold uppercase tracking-wider transition-colors"
                    >
                      <ShieldCheck size={11} /> Audit
                    </button>

                    {/* Impersonate */}
                    <div className="relative group/imp">
                      <button
                        onClick={() => hasConsent ? onImpersonate(org) : undefined}
                        disabled={!hasConsent}
                        className={cn(
                          'flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors',
                          hasConsent
                            ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 cursor-pointer'
                            : 'bg-zinc-100 text-zinc-400 cursor-not-allowed opacity-60',
                        )}
                      >
                        {hasConsent ? <ShieldCheck size={11} /> : <Lock size={11} />}
                        Login As
                      </button>
                      {!hasConsent && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-900 text-white text-[10px] font-medium rounded-xl whitespace-nowrap opacity-0 group-hover/imp:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg leading-snug text-center">
                          No support consent granted
                        </div>
                      )}
                    </div>

                    {/* Toggle status */}
                    <button
                      onClick={() => handleToggleStatus(org)}
                      title={org.status === OrgStatus.ACTIVE ? 'Suspend org' : 'Activate org'}
                      className={cn(
                        'p-1.5 rounded-lg transition-colors',
                        org.status === OrgStatus.ACTIVE
                          ? 'hover:bg-amber-50 text-amber-500 hover:text-amber-700'
                          : 'hover:bg-emerald-50 text-emerald-500 hover:text-emerald-700',
                      )}
                    >
                      {org.status === OrgStatus.ACTIVE ? <XCircle size={15} /> : <CheckCircle size={15} />}
                    </button>

                    {/* Delete */}
                    {canDeleteOrg && (
                    <button
                      onClick={() => handleDelete(org)}
                      title="Delete organisation"
                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                    )}
                  </div>

                  {/* Primary CTA — Inspect Module Data */}
                  <button
                    onClick={() => canInspect && onInspect(org)}
                    disabled={!canInspect}
                    title={canInspect ? 'Inspect tenant data' : 'You do not have permission to inspect tenant data'}
                    className={cn(
                      "flex items-center justify-center gap-1.5 w-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-colors",
                      canInspect
                        ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                        : "bg-zinc-200 text-zinc-400 cursor-not-allowed"
                    )}
                  >
                    <Eye size={11} /> Inspect
                  </button>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* ── Typed DELETE Confirmation Dialog ─────────────────────── */}
      {typedDeleteConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4 mx-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete Organisation</h3>
                <p className="text-sm text-slate-500">
                  This will permanently delete <span className="font-bold text-red-600">"{typedDeleteConfirm.org.name}"</span> and all associated users, API keys, and submissions.
                </p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">
                Type <span className="text-red-600">DELETE</span> to confirm
              </label>
              <input
                value={typedDeleteConfirm.typed}
                onChange={e => setTypedDeleteConfirm({ ...typedDeleteConfirm, typed: e.target.value })}
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                placeholder="DELETE"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setTypedDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors"
              >Cancel</button>
              <button
                onClick={confirmTypedDelete}
                disabled={typedDeleteConfirm.typed !== 'DELETE'}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors',
                  typedDeleteConfirm.typed === 'DELETE'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-zinc-200 text-zinc-400 cursor-not-allowed',
                )}
              >Delete Permanently</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Org Detail Drawer ────────────────────────────────────── */}
      {drawerOrg && (
        <OrgDetailDrawer org={drawerOrg} onClose={() => setDrawerOrg(null)} />
      )}
    </div>
  );
}

// (EditOrgDrawer removed — unified into OrgDrawer above)

// ─────────────────────────────────────────────────────────────────────────────
// Org Detail Drawer — Subscription & Module Summary
// ─────────────────────────────────────────────────────────────────────────────

function OrgDetailDrawer({ org, onClose }: { org: OrgWithStats; onClose: () => void }) {
  const enabledApps = org.enabledApps ?? [];
  const limits = PLAN_LIMITS[org.plan];
  const daysSinceSignup = Math.floor((Date.now() - (org.createdAt || Date.now())) / 86400000);
  const maxUsers = limits.usageLimits.max_users ?? 0;
  const userPct = maxUsers === Infinity ? 0 : Math.round((org.userCount / maxUsers) * 100);

  type DrawerTab = 'overview' | 'users' | 'logs';
  const [activeDrawerTab, setActiveDrawerTab] = useState<DrawerTab>('overview');
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load users when tab switches
  useEffect(() => {
    if (activeDrawerTab === 'users' && orgUsers.length === 0) {
      setLoadingUsers(true);
      repo.getUsersForOrg(org.id).then(users => {
        setOrgUsers(users);
        setLoadingUsers(false);
      });
    }
  }, [activeDrawerTab, org.id, orgUsers.length]);

  const DRAWER_TABS: { id: DrawerTab; label: string; icon: React.FC<{ size?: number }> }[] = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'users',    label: 'Users',    icon: Users },
    { id: 'logs',     label: 'Activity', icon: Activity },
  ];

  return (
    <div className="fixed inset-0 z-[150] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-200 bg-zinc-50">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <div className={cn(
                'w-2.5 h-2.5 rounded-full shrink-0',
                getOrgStatusMeta(org.status).dot,
              )} />
              <h2 className="text-lg font-black text-zinc-900 truncate">{org.name}</h2>
            </div>
            <p className="text-xs text-zinc-400 font-mono pl-4.5">{org.slug}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-200 transition-colors">
            <X size={18} className="text-zinc-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-zinc-200 px-6 pt-3">
          {DRAWER_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveDrawerTab(id)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-t-lg transition-all border-b-2 -mb-px',
                activeDrawerTab === id
                  ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                  : 'border-transparent text-zinc-400 hover:text-zinc-600',
              )}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* ── Overview Tab ──────────────────────────────────────── */}
          {activeDrawerTab === 'overview' && (
            <>
              {/* Subscription */}
              <section>
                <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Subscription</h3>
                <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Plan</span>
                    <PlanBadge plan={org.plan} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Status</span>
                    <StatusBadge status={org.status} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Created</span>
                    <span className="text-sm font-bold text-zinc-900">{new Date(org.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Age</span>
                    <span className="text-sm font-bold text-zinc-900">{daysSinceSignup} days</span>
                  </div>
                </div>
              </section>

              {/* Resource Usage */}
              <section>
                <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Resource Usage</h3>
                <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-zinc-600">Users</span>
                      <span className="text-sm font-bold text-zinc-900">{org.userCount} / {maxUsers === Infinity ? '∞' : maxUsers}</span>
                    </div>
                    {maxUsers !== Infinity && (
                      <div className="w-full bg-zinc-200 rounded-full h-1.5">
                        <div className={cn('h-1.5 rounded-full transition-all', userPct >= 80 ? 'bg-amber-500' : 'bg-indigo-500')} style={{ width: `${Math.min(userPct, 100)}%` }} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Hubs Limit</span>
                    <span className="text-sm font-bold text-zinc-900">{(limits.usageLimits.max_hubs ?? 0) === Infinity ? '∞' : (limits.usageLimits.max_hubs ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">API Access</span>
                    <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full', limits.apiAccess ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400')}>
                      {limits.apiAccess ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Active API Keys</span>
                    <span className="text-sm font-bold text-zinc-900">{org.apiKeyCount}</span>
                  </div>
                </div>
              </section>

              {/* Installed Modules */}
              <section>
                <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Installed Modules ({enabledApps.length})</h3>
                <div className="space-y-2">
                  {APP_REGISTRY.filter(a => a.status === 'live').map(app => {
                    const installed = enabledApps.includes(app.id);
                    return (
                      <div
                        key={app.id}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-xl border',
                          installed ? 'bg-white border-indigo-200' : 'bg-zinc-50 border-zinc-100 opacity-50',
                        )}
                      >
                        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', installed ? 'bg-indigo-100 text-indigo-600' : 'bg-zinc-100 text-zinc-400')}>
                          <AppIcon iconKey={app.iconKey} size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-zinc-900">{app.name}</p>
                          <p className="text-[10px] text-zinc-400 truncate">{app.description}</p>
                        </div>
                        {installed ? (
                          <CheckCircle size={16} className="text-emerald-500 shrink-0" />
                        ) : (
                          <XCircle size={16} className="text-zinc-300 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}

          {/* ── Users Tab ─────────────────────────────────────────── */}
          {activeDrawerTab === 'users' && (
            <section>
              <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">
                User Directory ({orgUsers.length})
              </h3>
              {loadingUsers ? (
                <div className="py-12 text-center text-zinc-400 text-sm">Loading users…</div>
              ) : orgUsers.length === 0 ? (
                <div className="py-12 text-center text-zinc-400 text-sm border border-dashed border-zinc-200 rounded-xl">
                  No users in this organisation.
                </div>
              ) : (
                <div className="space-y-2">
                  {orgUsers.map(u => (
                    <div key={u.id} className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-xl">
                      <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold shrink-0">
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-zinc-900 truncate">{u.name}</p>
                        <p className="text-[11px] text-zinc-400 truncate">{u.email}</p>
                      </div>
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest shrink-0',
                        u.role === UserRole.ADMIN ? 'bg-violet-100 text-violet-700' :
                        u.role === UserRole.SUPER_ADMIN ? 'bg-red-100 text-red-700' :
                        u.role === UserRole.DEPARTMENT_HEAD ? 'bg-purple-100 text-purple-700' :
                        u.role === UserRole.MANAGER ? 'bg-emerald-100 text-emerald-700' :
                        u.role === UserRole.EMPLOYEE ? 'bg-slate-100 text-slate-600' :
                        'bg-zinc-100 text-zinc-600',
                      )}>
                        {u.role?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── Activity / Logs Tab ───────────────────────────────── */}
          {activeDrawerTab === 'logs' && (
            <section>
              <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Usage Activity</h3>
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">Apps Installed</span>
                  <span className="font-bold text-zinc-900">{org.appCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">Total Users</span>
                  <span className="font-bold text-zinc-900">{org.userCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">API Keys Active</span>
                  <span className="font-bold text-zinc-900">{org.apiKeyCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">Days Active</span>
                  <span className="font-bold text-zinc-900">{daysSinceSignup}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">User Limit Usage</span>
                  <span className={cn('font-bold', userPct >= 80 ? 'text-amber-600' : 'text-zinc-900')}>{userPct}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">Support Access</span>
                  <span className={cn(
                    'text-xs font-bold px-2 py-0.5 rounded-full',
                    org.supportAccessEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400',
                  )}>
                    {org.supportAccessEnabled ? 'Granted' : 'Not Granted'}
                  </span>
                </div>
              </div>
              <p className="text-[10px] text-zinc-400 mt-4 text-center italic">
                Detailed event logs will be available in a future release.
              </p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Platform Health
// ─────────────────────────────────────────────────────────────────────────────

interface PlatformStats {
  totalOrgs:    number;
  totalApps:    number;
  totalUsers:   number;
  totalApiKeys: number;
  simulatedMRR: number;
  byPlan:       Record<OrgPlan, number>;
  topOrgs:      { name: string; appCount: number }[];
  moduleRequests: { pending: number; approved: number; rejected: number; total: number; recent: ModuleRequest[] };
  recentLogs: { id: string; userName: string; action: string; timestamp: number }[];
  moduleInstalls: { moduleId: string; moduleName: string; count: number }[];
}

function PlatformHealthTab({ userRole }: { userRole?: UserRole }) {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const navigate = useNavigate();

  const loadStats = useCallback(async () => {
    let allOrgs: Organization[] = [];
    let allUsers: User[] = [];
    let allApiKeys: ApiKey[] = [];

    allOrgs = (await repo.getOrganizations()).filter(o => o.id !== MASTER_ORG_ID);
    const userGroups = await Promise.all(allOrgs.map(async (org) => repo.getUsersForOrg(org.id)));
    allUsers = userGroups.flat();
    allApiKeys = await repo.getApiKeys().catch(() => []);

    const byPlan = Object.values(OrgPlan).reduce((acc, p) => {
      acc[p] = allOrgs.filter(o => o.plan === p).length;
      return acc;
    }, {} as Record<OrgPlan, number>);

    const PLAN_PRICES: Record<OrgPlan, number> = {
      [OrgPlan.FREE]: 0,
      [OrgPlan.BASIC]: 19,
      [OrgPlan.PRO]: 49,
      [OrgPlan.ENTERPRISE]: 299,
    };
    const simulatedMRR = allOrgs.reduce((sum, o) => sum + PLAN_PRICES[o.plan], 0);

    const totalApps = allOrgs.reduce((sum, o) => sum + (o.enabledApps ?? []).length, 0);

    const topOrgs = allOrgs
      .map(o => ({ name: o.name, appCount: (o.enabledApps ?? []).length }))
      .sort((a, b) => b.appCount - a.appCount)
      .slice(0, 5);

    // Module requests summary
    const allRequests: ModuleRequest[] = await repo.getModuleRequests();
    const pendingReqs  = allRequests.filter(r => r.status === ModuleRequestStatus.PENDING).length;
    const approvedReqs = allRequests.filter(r => r.status === ModuleRequestStatus.APPROVED).length;
    const rejectedReqs = allRequests.filter(r => r.status === ModuleRequestStatus.REJECTED).length;
    const recentRequests = [...allRequests].sort((a, b) => b.createdAt - a.createdAt).slice(0, 5);

    // Recent system activity
    const allLogs = await repo.getSystemLogs();
    const recentLogs = allLogs.slice(0, 5).map(l => ({ id: l.id, userName: l.userName, action: l.action, timestamp: l.timestamp }));

    // Module install popularity
    const installMap: Record<string, number> = {};
    for (const org of allOrgs) {
      for (const appId of (org.enabledApps ?? [])) {
        installMap[appId] = (installMap[appId] ?? 0) + 1;
      }
    }
    const moduleInstalls = Object.entries(installMap)
      .map(([moduleId, count]) => {
        const def = PLATFORM_MODULES.find(m => m.id === moduleId);
        return { moduleId, moduleName: def?.name ?? moduleId, count };
      })
      .sort((a, b) => b.count - a.count);

    setStats({
      totalOrgs:    allOrgs.length,
      totalApps,
      totalUsers:   allUsers.filter((u: User) => u.orgId && u.orgId !== '__platform__').length,
      totalApiKeys: allApiKeys.filter((k: ApiKey) => k.status === 'active').length,
      simulatedMRR,
      byPlan,
      topOrgs,
      moduleRequests: { pending: pendingReqs, approved: approvedReqs, rejected: rejectedReqs, total: allRequests.length, recent: recentRequests },
      recentLogs,
      moduleInstalls,
    });
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  if (!stats) return <div className="text-center py-12 text-zinc-400">Loading...</div>;

  const statCards = [
    { label: 'Organisations', value: stats.totalOrgs,    icon: Building2,    color: 'bg-indigo-50 text-indigo-600' },
    { label: 'Apps Installed', value: stats.totalApps,   icon: LayoutGrid,   color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Total Users',   value: stats.totalUsers,   icon: Users,        color: 'bg-blue-50 text-blue-600' },
    { label: 'Active API Keys', value: stats.totalApiKeys, icon: Key,        color: 'bg-amber-50 text-amber-600' },
    { label: 'Sim. MRR',     value: `$${stats.simulatedMRR}`, icon: DollarSign, color: 'bg-green-50 text-green-600' },
  ];

  const formatTimeAgo = (ts: number) => {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="space-y-8">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center mb-3', color)}>
              <Icon size={18} />
            </div>
            <div className="text-2xl font-black text-zinc-900">{value}</div>
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Module Requests Summary ─────────────────────────────────────── */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <h4 className="font-bold text-zinc-900 flex items-center gap-2">
            <Inbox size={16} className="text-zinc-400" /> Module Requests
          </h4>
          <button
            onClick={() => navigate('/saas-admin/requests')}
            className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          >
            View All <ChevronRight size={12} />
          </button>
        </div>

        {/* Request status counters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-zinc-50 rounded-xl px-4 py-3 text-center">
            <div className="text-xl font-black text-zinc-900">{stats.moduleRequests.total}</div>
            <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Total</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center">
            <div className="text-xl font-black text-amber-700">{stats.moduleRequests.pending}</div>
            <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">Pending</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center">
            <div className="text-xl font-black text-emerald-700">{stats.moduleRequests.approved}</div>
            <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Approved</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-center">
            <div className="text-xl font-black text-red-700">{stats.moduleRequests.rejected}</div>
            <div className="text-[10px] font-bold text-red-500 uppercase tracking-widest">Rejected</div>
          </div>
        </div>

        {/* Recent requests list */}
        {stats.moduleRequests.recent.length === 0 ? (
          <p className="text-sm text-zinc-400 italic text-center py-4">No module requests yet.</p>
        ) : (
          <div className="space-y-2">
            <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">Recent Requests</div>
            {stats.moduleRequests.recent.map(req => (
              <div key={req.id} className="flex items-center gap-3 px-3 py-2.5 bg-zinc-50 rounded-xl">
                <div className="shrink-0">
                  {req.status === ModuleRequestStatus.PENDING && <Clock size={14} className="text-amber-500" />}
                  {req.status === ModuleRequestStatus.APPROVED && <CheckCircle size={14} className="text-emerald-500" />}
                  {req.status === ModuleRequestStatus.REJECTED && <XCircle size={14} className="text-red-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-800 truncate">{req.orgName}</div>
                  <div className="text-xs text-zinc-500 truncate">Requested <span className="font-medium">{req.moduleName}</span></div>
                </div>
                <div className="text-[10px] text-zinc-400 font-medium shrink-0">{formatTimeAgo(req.createdAt)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan breakdown */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
          <h4 className="font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-zinc-400" /> Organisations by Plan
          </h4>
          <div className="space-y-3">
            {Object.values(OrgPlan).map(plan => {
              const count = stats.byPlan[plan] ?? 0;
              const pct   = stats.totalOrgs > 0 ? (count / stats.totalOrgs) * 100 : 0;
              return (
                <div key={plan} className="flex items-center gap-3">
                  <div className="w-20 shrink-0"><PlanBadge plan={plan} /></div>
                  <div className="flex-1 bg-zinc-100 rounded-full h-2">
                    <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-sm font-bold text-zinc-700 w-6 text-right">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top orgs by apps */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
          <h4 className="font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <LayoutGrid size={16} className="text-zinc-400" /> Top Organisations by Apps
          </h4>
          {stats.topOrgs.length === 0 ? (
            <p className="text-sm text-zinc-400 italic">No data yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.topOrgs.map((org, i) => (
                <div key={org.name} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-zinc-100 text-zinc-500 text-[10px] font-black flex items-center justify-center shrink-0">{i + 1}</div>
                  <div className="flex-1 font-medium text-zinc-800 text-sm truncate">{org.name}</div>
                  <div className="text-sm font-bold text-zinc-700">{org.appCount} apps</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Module Popularity ──────────────────────────────────────────── */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
          <h4 className="font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <Package size={16} className="text-zinc-400" /> Module Install Popularity
          </h4>
          {stats.moduleInstalls.length === 0 ? (
            <p className="text-sm text-zinc-400 italic">No modules installed yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.moduleInstalls.map(m => {
                const maxCount = stats.moduleInstalls[0]?.count ?? 1;
                const pct = (m.count / maxCount) * 100;
                return (
                  <div key={m.moduleId} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-zinc-800">{m.moduleName}</span>
                      <span className="text-xs font-bold text-zinc-500">{m.count} {m.count === 1 ? 'org' : 'orgs'}</span>
                    </div>
                    <div className="bg-zinc-100 rounded-full h-2">
                      <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Recent Activity ────────────────────────────────────────────── */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
          <h4 className="font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <Activity size={16} className="text-zinc-400" /> Recent Activity
          </h4>
          {stats.recentLogs.length === 0 ? (
            <p className="text-sm text-zinc-400 italic">No activity recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {stats.recentLogs.map(log => (
                <div key={log.id} className="flex items-start gap-3 px-3 py-2.5 bg-zinc-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">
                    {log.userName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-800 leading-snug">
                      <span className="font-semibold">{log.userName}</span>
                      {' '}
                      <span className="text-zinc-500">{log.action.length > 80 ? log.action.slice(0, 80) + '…' : log.action}</span>
                    </div>
                    <div className="text-[10px] text-zinc-400 font-medium mt-0.5">{formatTimeAgo(log.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── AI Usage Analytics ───────────────────────────────────────── */}
      <AiUsageSection />

      {/* ── Maintenance Section ─────────────────────────────────────────── */}
      <MaintenanceSection userRole={userRole} onStatsRefresh={() => {
        setStats(null);
        loadStats();
      }} />
    </div>
  );
}

// ─── AI Usage Analytics ──────────────────────────────────────────────────────

interface AiUsageData {
  today: { requests: number; characters: number };
  allTime: { requests: number; characters: number };
  byOrg: { orgId: string; orgName: string; requests: number; characters: number }[];
}

function AiUsageSection() {
  const [data, setData] = useState<AiUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const DAILY_FREE_LIMIT = 1500;

  useEffect(() => {
    (async () => {
      try {
        const { apiFetch } = await import('../data/ApiModuleRepository');
        const res = await apiFetch<{ data: AiUsageData }>('/api/system/ai-usage');
        setData(res.data);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm text-center text-zinc-400">Loading AI usage…</div>;
  if (!data) return null;

  const todayPct = Math.min((data.today.requests / DAILY_FREE_LIMIT) * 100, 100);
  const topOrg = data.byOrg[0];

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm space-y-6">
      <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
        <Bot size={16} className="text-violet-500" /> AI Usage Analytics
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-center">
          <div className="text-2xl font-black text-violet-700">{data.today.requests}</div>
          <div className="text-[10px] font-bold text-violet-500 uppercase tracking-widest">Today</div>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 text-center">
          <div className="text-2xl font-black text-indigo-700">{data.allTime.requests}</div>
          <div className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">All Time</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center">
          <div className="text-2xl font-black text-emerald-700">{(data.today.characters / 1000).toFixed(1)}K</div>
          <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Chars Today</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center">
          <div className="text-2xl font-black text-amber-700 truncate text-sm">{topOrg?.orgName ?? '—'}</div>
          <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">Top User</div>
        </div>
      </div>

      {/* Daily Limit Progress */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-zinc-500">Daily Free Limit ({DAILY_FREE_LIMIT} requests)</span>
          <span className="text-xs font-bold text-zinc-700">{data.today.requests} / {DAILY_FREE_LIMIT}</span>
        </div>
        <div className="bg-zinc-100 rounded-full h-3 overflow-hidden">
          <div
            className={cn(
              'h-3 rounded-full transition-all',
              todayPct >= 90 ? 'bg-red-500' : todayPct >= 70 ? 'bg-amber-500' : 'bg-violet-500',
            )}
            style={{ width: `${todayPct}%` }}
          />
        </div>
        {todayPct >= 90 && (
          <p className="text-[11px] text-red-500 font-medium mt-1.5 flex items-center gap-1">
            <Zap size={11} /> Approaching daily limit — consider upgrading to a paid Gemini plan.
          </p>
        )}
      </div>

      {/* Per-Org Usage Table */}
      {data.byOrg.length > 0 && (
        <div>
          <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">Usage by Organisation (Today)</div>
          <div className="border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Organisation</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Requests</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Characters</th>
                </tr>
              </thead>
              <tbody>
                {data.byOrg.map(row => (
                  <tr key={row.orgId} className="border-t border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-zinc-800">{row.orgName}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-600 font-bold">{row.requests}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-500">{row.characters.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Maintenance Section (Purge) ─────────────────────────────────────────────

function MaintenanceSection({ userRole, onStatsRefresh }: { userRole?: UserRole; onStatsRefresh: () => void }) {
  const toast = useToast();
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [purging, setPurging] = useState(false);
  const [gcRunning, setGcRunning] = useState(false);
  const [gcResult, setGcResult] = useState<{ total: number; orphaned: number; deleted: number; dryRun: boolean } | null>(null);

  const handleR2GarbageCollect = async (dryRun: boolean) => {
    setGcRunning(true);
    setGcResult(null);
    try {
      const { apiFetch } = await import('../data/ApiModuleRepository');
      const res = await apiFetch<{ data: { total: number; orphaned: number; deleted: number; dryRun: boolean }; message: string }>(
        `/api/system/r2-garbage-collect?dryRun=${dryRun}`, { method: 'POST' },
      );
      setGcResult(res.data);
      toast.success('R2 Scan Complete', res.message);
    } catch (e: any) {
      toast.error('R2 Scan Failed', e?.message ?? 'Unknown error');
    } finally {
      setGcRunning(false);
    }
  };

  const handlePurge = async () => {
    if (confirmText !== 'PURGE') return;
    // Only PLATFORM_OWNER may purge
    if (userRole !== UserRole.PLATFORM_OWNER) {
      toast.error('Access Denied', 'Only the Platform Owner can purge data.');
      return;
    }
    setPurging(true);
    setTimeout(() => {
      setPurging(false);
      setShowPurgeModal(false);
      setConfirmText('');
      onStatsRefresh();
      toast.success(
        'Operational Data Purged Successfully',
        'System configuration preserved. Module data, submissions, logs, and notifications have been removed.'
      );
    }, 600);
  };

  return (
    <>
      <div className="bg-white border border-red-200 rounded-2xl p-6 shadow-sm">
        <h4 className="font-bold text-zinc-900 mb-1 flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-500" /> Maintenance
        </h4>
        <p className="text-xs text-zinc-500 mb-4">Danger zone — destructive operations that cannot be undone.</p>
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-red-800">Purge All Operational Data</p>
            <p className="text-xs text-red-600 mt-0.5">
              Purges all operational module data, submissions, logs &amp; notifications. Preserves Users, Brands, Departments, and Settings.
            </p>
          </div>
          <button
            onClick={() => setShowPurgeModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-all shrink-0"
          >
            <Trash2 size={14} /> Purge Data
          </button>
        </div>

        {/* ── R2 Garbage Collector ── */}
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mt-3">
          <div>
            <p className="text-sm font-semibold text-amber-800">R2 Storage Garbage Collector</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Scans R2 bucket for orphaned files not referenced in any database table. Run a dry-run first to preview.
            </p>
            {gcResult && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-100 rounded-lg px-3 py-2 font-mono">
                Total: {gcResult.total} | Orphaned: {gcResult.orphaned} | Deleted: {gcResult.deleted}
                {gcResult.dryRun && <span className="ml-2 text-amber-600">(dry-run — no files deleted)</span>}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 shrink-0 ml-4">
            <button
              onClick={() => handleR2GarbageCollect(true)}
              disabled={gcRunning}
              className="flex items-center gap-2 px-4 py-1.5 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-600 transition-all disabled:opacity-50"
            >
              <HardDrive size={13} /> {gcRunning ? 'Scanning...' : 'Dry Run'}
            </button>
            <button
              onClick={() => handleR2GarbageCollect(false)}
              disabled={gcRunning || !gcResult?.dryRun || gcResult.orphaned === 0}
              className="flex items-center gap-2 px-4 py-1.5 bg-red-500 text-white rounded-xl text-xs font-bold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={13} /> Clean Orphans
            </button>
          </div>
        </div>
      </div>

      {/* Purge Confirmation Modal */}
      {showPurgeModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50" onClick={() => setShowPurgeModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <AlertTriangle size={20} /> Confirm Data Purge
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800 font-medium">
                  Warning: This will permanently delete all operational module data, submissions, and logs across the entire platform.
                </p>
                <p className="text-xs text-red-600 mt-2">
                  Settings, Users, Brands, and Departments will be preserved.
                </p>
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Type PURGE to confirm</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  className="mt-1 w-full px-4 py-2.5 border border-zinc-300 rounded-xl text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-red-400"
                  placeholder="PURGE"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowPurgeModal(false); setConfirmText(''); }}
                  className="flex-1 px-4 py-2.5 bg-zinc-100 text-zinc-700 rounded-xl text-sm font-semibold hover:bg-zinc-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurge}
                  disabled={confirmText !== 'PURGE' || purging}
                  className={cn(
                    'flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2',
                    confirmText === 'PURGE'
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                  )}
                >
                  <Trash2 size={14} /> {purging ? 'Purging...' : 'Purge All Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Subscriptions — Full CRUD with Sub-Tabs
// ─────────────────────────────────────────────────────────────────────────────

type SubsTabId = 'plans' | 'table-setup' | 'org-assignments' | 'module-quotas';

const SUBS_TABS: { id: SubsTabId; label: string }[] = [
  { id: 'plans',           label: 'Plans & Comparison' },
  { id: 'table-setup',     label: 'Table Setup' },
  { id: 'org-assignments', label: 'Org Assignments' },
  { id: 'module-quotas',   label: 'Module Quotas' },
];

// ── Plan Editor Modal (data-driven from Feature Registry) ────────────────────

function SubscriptionPlanModal({ plan, onClose, onSave }: {
  plan: SubscriptionPlan | null;
  onClose: () => void;
  onSave: (p: SubscriptionPlan) => void;
}) {
  const isNew = !plan;
  const [name, setName] = useState(plan?.name ?? '');
  const [price, setPrice] = useState(plan?.price ?? 0);
  const [features, setFeatures] = useState<PlatformFeature[]>([]);
  const [values, setValues] = useState<Record<string, number>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const [feats, allVals] = await Promise.all([repo.getPlatformFeatures(), repo.getPlanFeatureValues()]);
      setFeatures(feats.sort((a, b) => a.sortOrder - b.sortOrder));
      // Build values map from existing plan values, or default to 0
      const planId = plan?.id ?? '';
      const vMap: Record<string, number> = {};
      for (const f of feats) {
        const existing = allVals.find(v => v.planId === planId && v.featureId === f.id);
        vMap[f.id] = existing?.value ?? 0;
      }
      setValues(vMap);
    })();
  }, [plan?.id]);

  const setVal = (featureId: string, type: string, raw: string | boolean) => {
    if (type === 'BOOLEAN') {
      setValues(prev => ({ ...prev, [featureId]: raw ? 1 : 0 }));
    } else {
      const s = raw as string;
      const n = s === '∞' || s.toLowerCase() === 'infinity' ? Infinity : parseInt(s, 10);
      if (!isNaN(n) && n >= 0) setValues(prev => ({ ...prev, [featureId]: n }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Plan name is required.'); return; }
    const planId = plan?.id ?? `plan-${crypto.randomUUID().slice(0, 8)}`;
    // Save the plan entity
    onSave({
      id: planId,
      name: name.trim(),
      price,
      features: plan?.features ?? {
        apiAccess: false, webhookAccess: false,
        analyticsAccess: false, whiteLabelAccess: false, systemLogsAccess: false, maintenanceAccess: false,
        usageLimits: { max_users: 2 },
      },
      isSystem: plan?.isSystem,
      createdAt: plan?.createdAt ?? Date.now(),
    });
    // Save all feature values for this plan
    for (const f of features) {
      await repo.savePlanFeatureValue({
        id: `${planId}::${f.id}`,
        planId,
        featureId: f.id,
        value: values[f.id] ?? 0,
      });
    }
  };

  // Group features by category
  const categories = [...new Set<string>(features.map(f => f.category))];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-200">
          <h2 className="text-lg font-black text-zinc-900">
            {isNew ? 'Create Subscription Plan' : `Edit "${plan?.name}"`}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-100 transition-colors">
            <X size={18} className="text-zinc-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Plan Name *</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Business Pro"
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Monthly Price (USD)</label>
            <div className="relative">
              <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="number"
                min={0}
                step={1}
                value={price}
                onChange={e => setPrice(Math.max(0, Number(e.target.value)))}
                className="w-full rounded-xl border border-zinc-200 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Dynamic Feature Categories from Registry */}
          {categories.map(cat => {
            const catFeatures = features.filter(f => f.category === cat);
            return (
              <div key={cat}>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">{cat}</label>
                <div className="space-y-3">
                  {catFeatures.map(f => (
                    <div key={f.id} className="flex items-center justify-between">
                      <span className="text-sm text-zinc-700">{f.label}</span>
                      {f.type === 'BOOLEAN' ? (
                        <button
                          type="button"
                          onClick={() => setVal(f.id, 'BOOLEAN', !(values[f.id] === 1))}
                          className="flex items-center gap-1"
                        >
                          {values[f.id] === 1
                            ? <ToggleRight size={24} className="text-emerald-500" />
                            : <ToggleLeft size={24} className="text-zinc-300" />
                          }
                        </button>
                      ) : (
                        <input
                          type="text"
                          value={(values[f.id] ?? 0) === Infinity ? '∞' : String(values[f.id] ?? 0)}
                          onChange={e => setVal(f.id, 'NUMERIC', e.target.value)}
                          className="w-24 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
            >
              <Save size={14} /> {isNew ? 'Create Plan' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Feature Registry CRUD (Comparison Table Setup) ───────────────────────────

function FeatureRegistryModal({ feature, existingCategories, onClose, onSave }: {
  feature: PlatformFeature | null;
  existingCategories: string[];
  onClose: () => void;
  onSave: (f: PlatformFeature) => void;
}) {
  const isNew = !feature;
  const [id, setId] = useState(feature?.id ?? '');
  const [label, setLabel] = useState(feature?.label ?? '');
  const [type, setType] = useState<'BOOLEAN' | 'NUMERIC'>(feature?.type ?? 'BOOLEAN');
  const [category, setCategory] = useState(feature?.category ?? '');
  const [moduleId, setModuleId] = useState(feature?.moduleId ?? '');
  const [sortOrder, setSortOrder] = useState(feature?.sortOrder ?? 999);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) { setError('Label is required.'); return; }
    if (!category.trim()) { setError('Category is required.'); return; }
    const finalId = isNew
      ? (id.trim() || label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''))
      : feature!.id;
    onSave({
      id: finalId,
      label: label.trim(),
      type,
      category: category.trim(),
      moduleId: moduleId.trim() || undefined,
      sortOrder,
    });
  };

  const moduleOptions = PLATFORM_MODULES
    .filter(m => m.id !== 'system-tools')
    .map(m => ({ value: m.id, label: m.name }));

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-200 shrink-0">
          <h2 className="text-lg font-black text-zinc-900">{isNew ? 'Add Feature Row' : 'Edit Feature'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-100 transition-colors">
            <X size={18} className="text-zinc-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          {/* Feature ID */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">
              {isNew ? 'Feature ID (optional — auto-generated from label)' : 'Feature ID'}
            </label>
            {isNew ? (
              <input value={id} onChange={e => setId(e.target.value)} placeholder="e.g. support_response_time"
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            ) : (
              <div className="w-full rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-2.5 text-sm text-zinc-500 font-mono">{feature!.id}</div>
            )}
          </div>
          {/* Label */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Label *</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Support Response Time"
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          {/* Type */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Type *</label>
            <div className="flex gap-3">
              {(['BOOLEAN', 'NUMERIC'] as const).map(t => (
                <button key={t} type="button" onClick={() => setType(t)}
                  className={cn('flex-1 px-4 py-2 rounded-xl text-sm font-bold border transition-colors',
                    type === t ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50')}>
                  {t === 'BOOLEAN' ? '✓/✗ Boolean' : '# Numeric'}
                </button>
              ))}
            </div>
          </div>
          {/* Category — dropdown of existing + custom */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Category *</label>
            {existingCategories.length > 0 ? (
              <div className="space-y-2">
                <select
                  value={existingCategories.includes(category) ? category : '__custom__'}
                  onChange={e => { if (e.target.value !== '__custom__') setCategory(e.target.value); else setCategory(''); }}
                  className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                  {existingCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="__custom__">+ New Category…</option>
                </select>
                {!existingCategories.includes(category) && (
                  <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Enter new category name…"
                    className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                )}
              </div>
            ) : (
              <input value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Service Level"
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            )}
          </div>
          {/* Linked Module */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Linked Module (optional)</label>
            <select value={moduleId} onChange={e => setModuleId(e.target.value)}
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
              <option value="">— None (Platform-level) —</option>
              {moduleOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          {/* Sort Order */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Sort Order</label>
            <input type="number" min={0} value={sortOrder} onChange={e => setSortOrder(Math.max(0, Number(e.target.value)))}
              className="w-24 rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50">Cancel</button>
            <button type="submit" className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-zinc-900 text-white hover:bg-zinc-800">
              <Save size={14} /> {isNew ? 'Add Feature' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ComparisonTableSetupSubTab() {
  const [features, setFeatures] = useState<PlatformFeature[]>([]);
  const [editFeature, setEditFeature] = useState<PlatformFeature | null | 'new'>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const f = await repo.getPlatformFeatures();
    setFeatures(f.sort((a, b) => a.sortOrder - b.sortOrder));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (f: PlatformFeature) => {
    await repo.savePlatformFeature(f);
    setEditFeature(null);
    await load();
  };

  const handleDelete = async (id: string) => {
    await repo.deletePlatformFeature(id);
    setDeleteId(null);
    await load();
  };

  const moveFeature = async (featureId: string, direction: 'up' | 'down') => {
    const idx = features.findIndex(f => f.id === featureId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= features.length) return;
    // Swap sortOrder values
    const a = { ...features[idx], sortOrder: features[swapIdx].sortOrder };
    const b = { ...features[swapIdx], sortOrder: features[idx].sortOrder };
    await repo.savePlatformFeature(a);
    await repo.savePlatformFeature(b);
    await load();
  };

  // Group by category for display
  const categories = [...new Set<string>(features.map(f => f.category))];

  return (
    <div className="space-y-6">
      {editFeature !== null && (
        <FeatureRegistryModal
          feature={editFeature === 'new' ? null : editFeature}
          existingCategories={categories}
          onClose={() => setEditFeature(null)}
          onSave={handleSave}
        />
      )}

      {deleteId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" onClick={() => setDeleteId(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black text-zinc-900 mb-2">Delete Feature Row?</h3>
            <p className="text-sm text-zinc-500 mb-4">This will remove the row from the comparison table and delete all plan values for it.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-zinc-900">📋 Comparison Table Setup</h3>
            <p className="text-xs text-zinc-400">Define the rows shown in the Feature Comparison table. Add, edit, reorder, or remove features.</p>
          </div>
          <button
            onClick={() => setEditFeature('new')}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-colors"
          >
            <Plus size={14} /> Add Feature
          </button>
        </div>

        {categories.map(cat => (
          <div key={cat} className="mb-6">
            <div className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-2 px-1">{cat}</div>
            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    {['Order', 'ID', 'Label', 'Type', 'Module', 'Sort', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {features.filter(f => f.category === cat).map((f, idx, arr) => (
                    <tr key={f.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-2.5 w-20">
                        <div className="flex items-center gap-1">
                          <button onClick={() => moveFeature(f.id, 'up')} disabled={idx === 0}
                            className="p-1 rounded hover:bg-zinc-100 disabled:opacity-30 text-zinc-400"><ArrowUp size={12} /></button>
                          <button onClick={() => moveFeature(f.id, 'down')} disabled={idx === arr.length - 1}
                            className="p-1 rounded hover:bg-zinc-100 disabled:opacity-30 text-zinc-400"><ArrowDown size={12} /></button>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[10px] text-zinc-400 font-mono">{f.id}</td>
                      <td className="px-4 py-2.5 font-medium text-zinc-900">{f.label}</td>
                      <td className="px-4 py-2.5">
                        <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold',
                          f.type === 'BOOLEAN' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700')}>
                          {f.type}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-zinc-500 text-xs">
                        {f.moduleId ? PLATFORM_MODULES.find(m => m.id === f.moduleId)?.name ?? f.moduleId : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-[10px] text-zinc-400">{f.sortOrder}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-1">
                          <button onClick={() => setEditFeature(f)} className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"><Pencil size={13} /></button>
                          <button onClick={() => setDeleteId(f.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-zinc-400 hover:text-red-600"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {features.length === 0 && (
          <div className="text-center py-8 text-zinc-400 text-sm">No features defined. Add one to populate the comparison table.</div>
        )}
      </div>
    </div>
  );
}

// ── Organisation Plan Assignments Sub-Tab ────────────────────────────────────

function OrgAssignmentsSubTab() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [search, setSearch] = usePersistentState('search');

  const load = useCallback(async () => {
    const [p, o] = await Promise.all([repo.getSubscriptionPlans(), repo.getOrganizations()]);
    setPlans(p);
    setOrgs(o);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAssignPlan = async (org: Organization, planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    const planMap: Record<string, OrgPlan> = {
      [DEFAULT_PLAN_IDS.FREE]: OrgPlan.FREE,
      [DEFAULT_PLAN_IDS.BASIC]: OrgPlan.BASIC,
      [DEFAULT_PLAN_IDS.PRO]: OrgPlan.PRO,
      [DEFAULT_PLAN_IDS.ENTERPRISE]: OrgPlan.ENTERPRISE,
    };
    await repo.saveOrganization({
      ...org,
      subscriptionPlanId: planId,
      plan: planMap[planId] ?? org.plan,
    });
    await load();
  };

  const filtered = orgs.filter(o =>
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    o.slug?.toLowerCase().includes(search.toLowerCase()) ||
    o.id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-zinc-900">Organisation Plan Assignments</h3>
          <p className="text-xs text-zinc-400">Assign subscription plans to organisations. Search to filter.</p>
        </div>
        <div className="relative w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search organisations…"
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50">
              {['Organisation', 'Slug', 'Status', 'Current Plan', 'Change Plan'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filtered.map(org => {
              const currentPlan = plans.find(p => p.id === org.subscriptionPlanId);
              return (
                <tr key={org.id} className="hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium text-zinc-900">{org.name}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400 font-mono">{org.slug ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold',
                      org.status === OrgStatus.ACTIVE ? 'bg-emerald-50 text-emerald-700'
                      : org.status === OrgStatus.SUSPENDED ? 'bg-red-50 text-red-700'
                      : 'bg-zinc-100 text-zinc-500')}>
                      {org.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {currentPlan
                      ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700">{currentPlan.name}</span>
                      : <PlanBadge plan={org.plan} />
                    }
                  </td>
                  <td className="px-4 py-3">
                    <CustomSelect
                      value={org.subscriptionPlanId ?? ''}
                      onChange={v => handleAssignPlan(org, v)}
                      options={plans.map(p => ({ value: p.id, label: `${p.name}${p.price > 0 ? ` — $${p.price}/mo` : ' — Free'}` }))}
                      placeholder="Assign plan…"
                    />
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400 text-sm">{search ? 'No matching organisations.' : 'No organisations found.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Module Quota Drawer ──────────────────────────────────────────────────────

function ModuleQuotaDrawer({ moduleId, moduleName, metrics, onClose }: {
  moduleId: string;
  moduleName: string;
  metrics: { id: string; label: string }[];
  onClose: () => void;
}) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [edits, setEdits] = useState<Record<string, Record<string, number>>>({});

  useEffect(() => {
    (async () => {
      const [p, q] = await Promise.all([repo.getSubscriptionPlans(), repo.getModuleQuotas()]);
      setPlans(p);
      const init: Record<string, Record<string, number>> = {};
      for (const plan of p) {
        const existing = q.find(qq => qq.planId === plan.id && qq.moduleId === moduleId);
        init[plan.id] = {};
        for (const m of metrics) {
          init[plan.id][m.id] = existing?.limits[m.id] ?? 0;
        }
      }
      setEdits(init);
    })();
  }, [moduleId]);

  const setVal = (planId: string, metricId: string, raw: string) => {
    const n = raw === '∞' || raw.toLowerCase() === 'infinity' ? Infinity : parseInt(raw, 10);
    if (!isNaN(n) && n >= 0) {
      setEdits(prev => ({ ...prev, [planId]: { ...prev[planId], [metricId]: n } }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    for (const plan of plans) {
      const limits: Record<string, number> = {};
      for (const m of metrics) { limits[m.id] = edits[plan.id]?.[m.id] ?? 0; }
      await repo.saveModuleQuota({ id: `${plan.id}::${moduleId}`, planId: plan.id, moduleId, limits });
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-[200] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white w-full max-w-xl h-full shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-200 shrink-0">
          <div>
            <h2 className="text-lg font-black text-zinc-900">Configure Quotas</h2>
            <p className="text-xs text-zinc-500 mt-0.5">{moduleName} — per-plan limits</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-100 transition-colors"><X size={18} className="text-zinc-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {plans.map(plan => (
            <div key={plan.id} className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                <span className="text-sm font-bold text-zinc-900">{plan.name}</span>
                <span className="text-[10px] font-bold text-zinc-400">{plan.price === 0 ? 'Free' : `$${plan.price}/mo`}</span>
              </div>
              <div className="px-4 py-3 space-y-3">
                {metrics.map(m => (
                  <div key={m.id} className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">{m.label}</span>
                    <input type="text"
                      value={(edits[plan.id]?.[m.id] ?? 0) === Infinity ? '∞' : String(edits[plan.id]?.[m.id] ?? 0)}
                      onChange={e => setVal(plan.id, m.id, e.target.value)}
                      className="w-24 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-zinc-200 px-6 py-4 flex gap-3 shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">
            <Save size={14} /> {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Quotas'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModuleQuotasSubTab() {
  const quotableModules = PLATFORM_MODULES.filter(m => m.id !== 'system-tools' && m.id !== 'test-app' && m.metrics && m.metrics.length > 0);
  const [drawerModule, setDrawerModule] = useState<typeof quotableModules[number] | null>(null);

  const getIconKey = (moduleId: string): string => {
    const app = APP_REGISTRY.find(a => a.moduleId === moduleId);
    return app?.iconKey ?? 'Package';
  };

  return (
    <div className="space-y-6">
      {drawerModule && (
        <ModuleQuotaDrawer moduleId={drawerModule.id} moduleName={drawerModule.name}
          metrics={drawerModule.metrics ?? []} onClose={() => setDrawerModule(null)} />
      )}
      <div>
        <div className="mb-4">
          <h3 className="text-base font-bold text-zinc-900">Module Quota Configuration</h3>
          <p className="text-xs text-zinc-400">Configure per-plan usage limits for each module independently.</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50">
                {['Module', 'Metrics', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {quotableModules.map(mod => (
                <tr key={mod.id} className="hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-500">
                        <AppIcon iconKey={getIconKey(mod.id)} size={16} />
                      </div>
                      <div>
                        <span className="font-bold text-zinc-900">{mod.name}</span>
                        <span className="block text-[10px] text-zinc-400">{mod.id}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {(mod.metrics ?? []).map(m => (
                        <span key={m.id} className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold">{m.label}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setDrawerModule(mod)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 text-zinc-700 rounded-lg text-xs font-bold hover:bg-zinc-200 transition-colors">
                      ⚙️ Configure Quotas
                    </button>
                  </td>
                </tr>
              ))}
              {quotableModules.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-zinc-400 text-sm">No modules with configurable metrics found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main Subscriptions Tab (sub-tab host) ────────────────────────────────────

function SubscriptionsTab() {
  const [subTab, setSubTab] = useState<SubsTabId>('plans');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [features, setFeatures] = useState<PlatformFeature[]>([]);
  const [planValues, setPlanValues] = useState<PlanFeatureValue[]>([]);
  const [editPlan, setEditPlan] = useState<SubscriptionPlan | null | 'new'>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<SubscriptionPlan | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);

  const load = useCallback(async () => {
    const [p, o, f, v] = await Promise.all([
      repo.getSubscriptionPlans(), repo.getOrganizations(),
      repo.getPlatformFeatures(), repo.getPlanFeatureValues(),
    ]);
    setPlans(p);
    setOrgs(o);
    setFeatures(f.sort((a, b) => a.sortOrder - b.sortOrder));
    setPlanValues(v);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSavePlan = async (plan: SubscriptionPlan) => {
    await repo.saveSubscriptionPlan(plan);
    setEditPlan(null);
    await load();
  };

  const handleDeletePlan = async (plan: SubscriptionPlan) => {
    const using = orgs.filter(o => o.subscriptionPlanId === plan.id);
    if (using.length > 0) { setDeleteConfirm(null); return; }
    await repo.deleteSubscriptionPlan(plan.id);
    setDeleteConfirm(null);
    await load();
  };

  const orgsUsingPlan = (planId: string) => orgs.filter(o => o.subscriptionPlanId === planId).length;

  // Feature value lookup helper
  const getVal = (planId: string, featureId: string): number => {
    return planValues.find(v => v.planId === planId && v.featureId === featureId)?.value ?? 0;
  };

  // Group features by category
  const categories = [...new Set<string>(features.map(f => f.category))];

  return (
    <div className="space-y-6">
      {editPlan !== null && (
        <SubscriptionPlanModal
          plan={editPlan === 'new' ? null : editPlan}
          onClose={() => setEditPlan(null)}
          onSave={handleSavePlan}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" onClick={() => setDeleteConfirm(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black text-zinc-900 mb-2">Delete Plan?</h3>
            {orgsUsingPlan(deleteConfirm.id) > 0 ? (
              <>
                <p className="text-sm text-red-600 mb-4">
                  <AlertTriangle size={14} className="inline mr-1" />
                  Cannot delete — {orgsUsingPlan(deleteConfirm.id)} organisation(s) still use this plan. Reassign them first.
                </p>
                <button onClick={() => setDeleteConfirm(null)} className="w-full px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50">Close</button>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-500 mb-4">This will permanently remove the "{deleteConfirm.name}" plan.</p>
                <div className="flex gap-3">
                  <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50">Cancel</button>
                  <button onClick={() => handleDeletePlan(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700">Delete</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Sub-Tab Bar ── */}
      <div className="flex gap-6 border-b border-zinc-200">
        {SUBS_TABS.map(({ id, label }) => (
          <button key={id} onClick={() => { setSubTab(id); if (id === 'plans') load(); }}
            className={cn('pb-2 text-xs font-semibold transition-all border-b-2 -mb-px',
              subTab === id ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600')}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Plans & Comparison ── */}
      {subTab === 'plans' && (
        <div className="space-y-8">
          {/* Subscription Plans CRUD */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-zinc-900">Subscription Plans</h3>
                <p className="text-xs text-zinc-400">Manage pricing tiers and feature permissions for all organisations.</p>
              </div>
              <button onClick={() => setEditPlan('new')}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-colors">
                <Plus size={14} /> New Plan
              </button>
            </div>
            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    {['Plan Name', 'Price', 'Orgs Using', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {plans.map(plan => (
                    <tr key={plan.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-3">
                        <span className="font-bold text-zinc-900">{plan.name}</span>
                        {plan.isSystem && <span className="ml-2 text-[9px] font-bold text-zinc-400 uppercase">System</span>}
                      </td>
                      <td className="px-4 py-3 text-zinc-700 font-medium">
                        {plan.price === 0 ? <span className="text-zinc-400">Free</span> : `$${plan.price}/mo`}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold',
                          orgsUsingPlan(plan.id) > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-zinc-100 text-zinc-400')}>
                          {orgsUsingPlan(plan.id)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => setEditPlan(plan)} title="Edit Plan"
                            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"><Pencil size={14} /></button>
                          {!plan.isSystem && (
                            <button onClick={() => setDeleteConfirm(plan)} title="Delete Plan"
                              className="p-1.5 rounded-lg hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {plans.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-400 text-sm">No subscription plans configured.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Feature Comparison Matrix (100% Data-Driven) ── */}
          <div>
            <h3 className="text-base font-bold text-zinc-900 mb-3">Feature Comparison</h3>
            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm overflow-x-auto">
              <div className="grid" style={{ gridTemplateColumns: `220px repeat(${plans.length}, 1fr)` }}>
                {/* Column headers */}
                <div className="px-4 py-3 bg-zinc-50 text-[10px] font-bold text-zinc-500 uppercase tracking-widest border-b border-zinc-200">Feature</div>
                {plans.map(p => (
                  <div key={p.id} className="px-4 py-3 bg-zinc-50 text-center border-b border-zinc-200">
                    <span className="text-xs font-bold text-zinc-700">{p.name}</span>
                    {p.price > 0 && <span className="block text-[10px] text-zinc-400">${p.price}/mo</span>}
                  </div>
                ))}

                {/* Dynamic rows grouped by category */}
                {categories.map(cat => (
                  <React.Fragment key={cat}>
                    {/* Category sub-header */}
                    <div className="px-4 py-2 bg-zinc-100 text-[10px] font-black text-zinc-500 uppercase tracking-widest border-b border-zinc-200"
                      style={{ gridColumn: `1 / -1` }}>
                      {cat}
                    </div>
                    {/* Feature rows in this category */}
                    {features.filter(f => f.category === cat).map(f => (
                      <React.Fragment key={f.id}>
                        <div className="px-4 py-3 text-sm text-zinc-600 font-medium border-b border-zinc-50">{f.label}</div>
                        {plans.map(p => {
                          const val = getVal(p.id, f.id);
                          return (
                            <div key={p.id} className="px-4 py-3 text-center text-sm border-b border-zinc-50">
                              {f.type === 'BOOLEAN'
                                ? (val === 1
                                  ? <CheckCircle size={15} className="inline text-emerald-500" />
                                  : <XCircle size={15} className="inline text-zinc-300" />)
                                : (val === 0
                                  ? <XCircle size={15} className="inline text-zinc-300" />
                                  : val === Infinity || val >= 999999
                                    ? <span className="text-xs font-bold text-emerald-600">Unlimited</span>
                                    : <span className="text-zinc-700 font-medium">{val}</span>)
                              }
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Comparison Table Setup ── */}
      {subTab === 'table-setup' && <ComparisonTableSetupSubTab />}

      {/* ── Org Assignments ── */}
      {subTab === 'org-assignments' && <OrgAssignmentsSubTab />}

      {/* ── Module Quotas ── */}
      {subTab === 'module-quotas' && <ModuleQuotasSubTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Org Approvals
// ─────────────────────────────────────────────────────────────────────────────

function OrgApprovalsTab({ currentUser }: { currentUser?: { id?: string; name?: string } | null }) {
  const toast = useToast();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingOrgId, setProcessingOrgId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const allOrgs = await repo.getOrganizations();
      setOrgs(allOrgs.filter(org => org.id !== MASTER_ORG_ID));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pendingOrgs = orgs.filter(org => org.status === OrgStatus.PENDING_APPROVAL || org.status === OrgStatus.PENDING_PAYMENT);
  const rejectedOrgs = orgs.filter(org => org.status === OrgStatus.REJECTED);

  const notifyTenantAdmins = async (org: Organization, approved: boolean) => {
    const orgUsers = await repo.getUsersForOrg(org.id).catch(() => []);
    const admins = orgUsers.filter(user => user.role === UserRole.ADMIN);
    for (const admin of admins) {
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: admin.id,
        type: approved ? NotificationType.APPROVED : NotificationType.WARNING,
        title: approved ? 'Organization Approved' : 'Organization Rejected',
        message: approved
          ? `${org.name} is now active. You can sign in and start configuring your workspace.`
          : `${org.name} was not approved. Contact the platform team for next steps.`,
        targetPath: approved ? '/' : '/login',
        isRead: false,
        createdAt: Date.now(),
        orgId: org.id,
      });
    }
  };

  const handleDecision = async (org: Organization, nextStatus: OrgStatus) => {
    setProcessingOrgId(org.id);
    try {
      await repo.saveOrganization({ ...org, status: nextStatus });

      // When approving, also activate the org's admin users
      if (nextStatus === OrgStatus.ACTIVE) {
        const orgUsers = await repo.getUsersForOrg(org.id).catch(() => []);
        for (const u of orgUsers) {
          if (u.role === UserRole.ADMIN && u.status !== UserStatus.ACTIVE) {
            await repo.saveUser({ ...u, status: UserStatus.ACTIVE });
          }
        }
      }

      await notifyTenantAdmins(org, nextStatus === OrgStatus.ACTIVE);
      if (currentUser?.id && currentUser?.name) {
        await repo.saveSystemLog({
          id: crypto.randomUUID(),
          userId: currentUser.id,
          userName: currentUser.name,
          action: `ORG_${nextStatus === OrgStatus.ACTIVE ? 'APPROVED' : 'REJECTED'} — ${org.name}`,
          orgId: org.id,
          timestamp: Date.now(),
        });
      }
      toast.success(
        nextStatus === OrgStatus.ACTIVE ? 'Organization Approved' : 'Organization Rejected',
        `${org.name} has been updated to ${getOrgStatusMeta(nextStatus).label}.`,
      );
      await load();
    } finally {
      setProcessingOrgId(null);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Loading approvals...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-black text-zinc-900 mb-1">Organization Approvals</h2>
        <p className="text-sm text-zinc-500">Review pending workspace requests before they can sign in.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
          <div className="text-2xl font-black text-zinc-900">{pendingOrgs.length}</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mt-1">Pending</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
          <div className="text-2xl font-black text-rose-700">{rejectedOrgs.length}</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mt-1">Rejected</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
          <div className="text-2xl font-black text-emerald-700">{orgs.filter(org => org.status === OrgStatus.ACTIVE).length}</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mt-1">Active</div>
        </div>
      </div>

      {pendingOrgs.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-2xl p-10 text-center text-sm text-zinc-400">
          No pending organization approvals.
        </div>
      ) : (
        <div className="space-y-4">
          {pendingOrgs.map(org => {
            const processing = processingOrgId === org.id;
            const planLabel = PLAN_LABELS[org.plan] ?? org.plan;
            return (
              <div key={org.id} className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-base font-black text-zinc-900">{org.name}</h3>
                    <StatusBadge status={org.status} />
                    <PlanBadge plan={org.plan} />
                  </div>
                  <p className="text-xs text-zinc-400 font-mono">{org.slug}</p>
                  <p className="text-sm text-zinc-500 mt-2">
                    Requested {planLabel} on {new Date(org.createdAt).toLocaleDateString()}
                    {org.industry ? ` • ${org.industry}` : ''}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleDecision(org, OrgStatus.REJECTED)}
                    disabled={processing}
                    className="px-4 py-2 rounded-xl border border-rose-200 text-rose-600 text-sm font-bold hover:bg-rose-50 disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleDecision(org, OrgStatus.ACTIVE)}
                    disabled={processing}
                    className="px-4 py-2 rounded-xl bg-zinc-900 text-white text-sm font-bold hover:bg-zinc-800 disabled:opacity-50"
                  >
                    {processing ? 'Saving...' : (org.status === OrgStatus.PENDING_PAYMENT ? 'Mark Paid & Activate' : 'Approve & Activate')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Global Settings
// ─────────────────────────────────────────────────────────────────────────────

function GlobalSettingsTab() {
  const [settings, setSettings] = useState<PlatformSettings>({
    platformName: 'ClickIt Platform',
    allowSignups: true,
    autoApproveNewOrganizations: false,
  });
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    repo.getPlatformSettings()
      .then(setSettings)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    await repo.savePlatformSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Loading settings...</div>;
  }

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-black text-zinc-900 mb-1">Global Settings</h2>
        <p className="text-sm text-zinc-500">Platform-wide onboarding and activation controls.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-6 shadow-sm">
        <h3 className="font-bold text-zinc-900 text-base">Platform Identity</h3>

        <div>
          <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">
            Platform Name
          </label>
          <input
            value={settings.platformName}
            onChange={e => setSettings(prev => ({ ...prev, platformName: e.target.value }))}
            className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-zinc-400 mt-1">Shown in the sidebar header and login page.</p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-zinc-900">Allow New Signups</p>
            <p className="text-xs text-zinc-400 mt-0.5">Allow organisations to sign up from the public landing page.</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, allowSignups: !prev.allowSignups }))}
            className="text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            {settings.allowSignups
              ? <ToggleRight size={32} className="text-emerald-500" />
              : <ToggleLeft size={32} className="text-zinc-400" />
            }
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-zinc-900">Auto-approve new organizations</p>
            <p className="text-xs text-zinc-400 mt-0.5">Free-plan signups activate immediately when enabled. Paid plans still wait for payment review.</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, autoApproveNewOrganizations: !prev.autoApproveNewOrganizations }))}
            className="text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            {settings.autoApproveNewOrganizations
              ? <ToggleRight size={32} className="text-emerald-500" />
              : <ToggleLeft size={32} className="text-zinc-400" />
            }
          </button>
        </div>

        <div className="pt-2">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-all"
          >
            {saved
              ? <><CheckCircle size={15} className="text-emerald-400" /> Saved!</>
              : <><Save size={15} /> Save Settings</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Module Requests (Approval Command Center)
// ─────────────────────────────────────────────────────────────────────────────

function ModuleRequestsTab({ currentUser }: { currentUser?: { id?: string; name?: string } | null }) {
  const [requests, setRequests] = useState<ModuleRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ModuleRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'PENDING' | 'APPROVED' | 'REJECTED'>('all');
  const toast = useToast();

  const load = useCallback(async () => {
    const data = await repo.getModuleRequests();
    setRequests(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const pendingCount = requests.filter(r => r.status === ModuleRequestStatus.PENDING).length;

  const handleApprove = async (req: ModuleRequest) => {
    setProcessing(true);

    // 1. Update request status
    const updated: ModuleRequest = { ...req, status: ModuleRequestStatus.APPROVED, resolvedAt: Date.now(), adminNotes: 'Approved' };
    await repo.saveModuleRequest(updated);

    // 2. Auto-enable the module on the org
    const org = await repo.getOrganizationById(req.orgId);
    if (org) {
      const apps = org.enabledApps ?? [];
      if (!apps.includes(req.moduleId)) {
        await repo.saveOrganization({ ...org, enabledApps: [...apps, req.moduleId] });
      }
    }

    // 3. Notify the requesting org admin
    const allUsers = await repo.getUsers();
    const orgAdmins = allUsers.filter(u => u.orgId === req.orgId && (u.role === UserRole.ADMIN || u.id === req.requestedBy));
    const notifiedIds = new Set<string>();
    for (const admin of orgAdmins) {
      if (notifiedIds.has(admin.id)) continue;
      notifiedIds.add(admin.id);
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: admin.id,
        actorId: currentUser?.id,
        type: NotificationType.APPROVED,
        title: `Access Granted: ${req.moduleName}`,
        message: `${req.moduleName} is now available in your portal. Go to User Accounts to assign it to your team.`,
        targetPath: '/system/users',
        isRead: false,
        createdAt: Date.now(),
        orgId: req.orgId,
      });
    }

    // 4. Audit log
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: currentUser?.id ?? 'super-admin',
      userName: currentUser?.name ?? 'Super Admin',
      action: `MODULE_REQUEST_APPROVED — "${req.moduleName}" enabled for "${req.orgName}" (${req.orgId})`,
      timestamp: Date.now(),
      orgId: '__platform__',
    });

    setSelected(null);
    setProcessing(false);
    toast.success('Request Approved', `${req.moduleName} has been enabled for ${req.orgName}.`);
    await load();
  };

  const handleReject = async (req: ModuleRequest) => {
    if (!rejectReason.trim()) return;
    setProcessing(true);

    // 1. Update request status
    const updated: ModuleRequest = { ...req, status: ModuleRequestStatus.REJECTED, resolvedAt: Date.now(), adminNotes: rejectReason.trim() };
    await repo.saveModuleRequest(updated);

    // 2. Notify the requesting org admin
    const allUsers = await repo.getUsers();
    const orgAdmins = allUsers.filter(u => u.orgId === req.orgId && (u.role === UserRole.ADMIN || u.id === req.requestedBy));
    const notifiedIds = new Set<string>();
    for (const admin of orgAdmins) {
      if (notifiedIds.has(admin.id)) continue;
      notifiedIds.add(admin.id);
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: admin.id,
        actorId: currentUser?.id,
        type: NotificationType.REJECTED,
        title: `Request Declined: ${req.moduleName}`,
        message: `Reason: ${rejectReason.trim()}`,
        targetPath: '/system/marketplace',
        isRead: false,
        createdAt: Date.now(),
        orgId: req.orgId,
      });
    }

    // 3. Audit log
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: currentUser?.id ?? 'super-admin',
      userName: currentUser?.name ?? 'Super Admin',
      action: `MODULE_REQUEST_REJECTED — "${req.moduleName}" denied for "${req.orgName}" (${req.orgId}). Reason: ${rejectReason.trim()}`,
      timestamp: Date.now(),
      orgId: '__platform__',
    });

    setSelected(null);
    setRejectReason('');
    setProcessing(false);
    toast.success('Request Rejected', `${req.orgName}'s request for ${req.moduleName} has been declined.`);
    await load();
  };

  const statusBadge = (status: ModuleRequestStatus) => {
    switch (status) {
      case ModuleRequestStatus.PENDING:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200"><Clock size={9} /> Pending</span>;
      case ModuleRequestStatus.APPROVED:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle size={9} /> Approved</span>;
      case ModuleRequestStatus.REJECTED:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-red-100 text-red-700 border border-red-200"><XCircle size={9} /> Rejected</span>;
    }
  };

  if (loading) return <div className="py-16 text-center text-zinc-400 italic">Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            Module Requests
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                {pendingCount} pending
              </span>
            )}
          </h3>
          <p className="text-sm text-zinc-500">Review and manage module access requests from organisations.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Filter:</span>
          <div className="flex gap-1 bg-zinc-100 rounded-lg p-0.5">
            {(['all', 'PENDING', 'APPROVED', 'REJECTED'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all',
                  filter === f ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                )}
              >
                {f === 'all' ? 'All' : f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-zinc-400 italic text-sm border border-dashed border-zinc-200 rounded-2xl">
          {filter === 'all' ? 'No module requests yet.' : `No ${filter.toLowerCase()} requests.`}
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 text-left">
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Status</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Organization</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Module</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Message</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Date</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(req => (
                <tr
                  key={req.id}
                  className={cn(
                    'border-b border-zinc-50 transition-colors',
                    req.status === ModuleRequestStatus.PENDING ? 'hover:bg-amber-50/50 cursor-pointer' : 'hover:bg-zinc-50/50'
                  )}
                  onClick={() => req.status === ModuleRequestStatus.PENDING && setSelected(req)}
                >
                  <td className="px-5 py-3">{statusBadge(req.status)}</td>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-zinc-900">{req.orgName}</div>
                    <div className="text-[10px] text-zinc-400">{req.requestedByName}</div>
                  </td>
                  <td className="px-5 py-3 font-medium text-zinc-700">{req.moduleName}</td>
                  <td className="px-5 py-3 text-zinc-500 max-w-[200px] truncate">{req.message || '—'}</td>
                  <td className="px-5 py-3 text-zinc-400 text-xs whitespace-nowrap">{new Date(req.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-3">
                    {req.status === ModuleRequestStatus.PENDING ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelected(req); }}
                        className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-indigo-700 transition-colors"
                      >
                        Review
                      </button>
                    ) : (
                      <span className="text-[10px] text-zinc-400 italic">{req.adminNotes || '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-zinc-100">
              <div>
                <h2 className="text-lg font-bold text-zinc-900">Review Request</h2>
                <p className="text-sm text-zinc-500 mt-0.5">{selected.orgName} &middot; {selected.moduleName}</p>
              </div>
              <button onClick={() => { setSelected(null); setRejectReason(''); }} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Details */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Organization</p>
                  <p className="text-sm font-semibold text-zinc-900">{selected.orgName}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Module</p>
                  <p className="text-sm font-semibold text-zinc-900">{selected.moduleName}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Requested By</p>
                  <p className="text-sm text-zinc-700">{selected.requestedByName}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Date</p>
                  <p className="text-sm text-zinc-700">{new Date(selected.createdAt).toLocaleString()}</p>
                </div>
              </div>

              {selected.message && (
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Message</p>
                  <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-700 leading-relaxed">
                    {selected.message}
                  </div>
                </div>
              )}

              {/* Rejection reason input */}
              <div>
                <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">
                  Rejection Reason (required to reject)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g. Payment not received, plan upgrade required..."
                  rows={3}
                  className="w-full px-4 py-3 text-sm border border-zinc-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none resize-none"
                  maxLength={300}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3 p-6 border-t border-zinc-100">
              <button
                onClick={() => { setSelected(null); setRejectReason(''); }}
                className="px-4 py-2.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleReject(selected)}
                  disabled={processing || !rejectReason.trim()}
                  className="px-5 py-2.5 text-sm font-bold bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <XCircle size={14} /> Reject
                </button>
                <button
                  onClick={() => handleApprove(selected)}
                  disabled={processing}
                  className="px-5 py-2.5 text-sm font-bold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <CheckCircle size={14} /> {processing ? 'Processing...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Module Catalog — Platform Features Gallery + Cross-Manage Access
// ─── Platform Maintenance Tab ────────────────────────────────────────────────

interface PlatformOverview {
  totalBackups: number;
  totalWipes: number;
  totalStorageMB: number;
  storageBreakdown: { folder: string; label: string; bytes: number; mb: number }[];
  orgHealth: {
    orgId: string; orgName: string; plan: string; status: string; createdAt: number;
    lastBackup: number | null; moduleCounts: Record<string, number>;
  }[];
}

function PlatformMaintenanceTab({ onInspect }: { onInspect: (org: Organization) => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [backingUp, setBackingUp] = useState(false);
  const [purgeModal, setPurgeModal] = useState<{ orgId: string; orgName: string } | null>(null);
  const [purgePassword, setPurgePassword] = useState('');
  const [purging, setPurging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const { apiFetch } = await import('../data/ApiModuleRepository');
      const res = await apiFetch<{ data: PlatformOverview }>('/api/maintenance/platform/overview');
      setOverview(res.data);
    } catch (err: any) {
      const msg = err.message ?? 'Could not fetch platform maintenance overview.';
      setLoadError(msg);
      toast.error('Load Failed', msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const handleGlobalBackup = async () => {
    setBackingUp(true);
    try {
      const { getAuthToken } = await import('../data/ApiModuleRepository');
      const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/api/maintenance/platform/global-backup`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { const err = await res.json().catch(() => null); throw new Error((err as any)?.message ?? 'Backup failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] ?? `clickit-global-${Date.now()}.clickit-global`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Global Backup Complete', 'The entire platform has been archived.');
    } catch (err: any) {
      toast.error('Backup Failed', err.message ?? 'Global backup could not be generated.');
    } finally {
      setBackingUp(false);
    }
  };

  const handlePurge = async () => {
    if (!purgeModal || !purgePassword) return;
    setPurging(true);
    try {
      const { apiFetch } = await import('../data/ApiModuleRepository');
      const res = await apiFetch<{ data: { orgName: string; totalRowsDeleted: number; totalAssetsDeleted: number } }>('/api/maintenance/platform/purge-org', {
        method: 'POST',
        body: JSON.stringify({ orgId: purgeModal.orgId, password: purgePassword }),
      });
      toast.success('Purge Complete', `${res.data.orgName}: ${res.data.totalRowsDeleted} rows + ${res.data.totalAssetsDeleted} assets deleted.`);
      setPurgeModal(null);
      setPurgePassword('');
      await loadOverview();
    } catch (err: any) {
      toast.error('Purge Failed', err.message ?? 'Could not purge organization data.');
    } finally {
      setPurging(false);
    }
  };

  const handleQuickInspect = (orgId: string, orgName: string) => {
    // Navigate to God Mode inspector for this org's maintenance page
    navigate(`/saas-admin/inspect/${orgId}`);
  };

  const formatTimeAgo = (ts: number) => {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (loading) return <div className="text-center py-12 text-zinc-400">Loading platform maintenance data…</div>;
  if (!overview) return (
    <div className="text-center py-12">
      <div className="inline-block bg-red-500/10 border border-red-500/30 rounded-xl px-8 py-6 max-w-md">
        <p className="text-red-400 font-semibold text-lg mb-2">Failed to load overview</p>
        <p className="text-zinc-400 text-sm mb-4">{loadError ?? 'An unexpected error occurred. The server may be temporarily unavailable.'}</p>
        <button onClick={loadOverview} className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors">
          Retry
        </button>
      </div>
    </div>
  );

  const filteredOrgs = overview.orgHealth.filter(o =>
    o.orgName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.orgId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalDataItems = overview.orgHealth.reduce((s, o) => s + Object.values(o.moduleCounts).reduce((a, b) => a + b, 0), 0);

  return (
    <div className="space-y-8">
      {/* Header + Global Backup */}
      <div className="flex items-end justify-between">
        <div>
          <h3 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <HardDrive size={20} className="text-emerald-600" /> Platform Maintenance
          </h3>
          <p className="text-sm text-zinc-500 mt-1">Cross-tenant backup, recovery, and churn management.</p>
        </div>
        <button onClick={handleGlobalBackup} disabled={backingUp}
          className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 disabled:opacity-50 transition-colors">
          <Package size={14} /> {backingUp ? 'Generating…' : '📦 Generate Global Platform Backup'}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
            <HardDrive size={18} />
          </div>
          <div className="text-2xl font-black text-zinc-900">{overview.totalBackups}</div>
          <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">Total Backups</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-emerald-50 text-emerald-600">
            <Activity size={18} />
          </div>
          <div className="text-2xl font-black text-zinc-900">{overview.totalStorageMB} <span className="text-sm font-bold text-zinc-400">MB</span></div>
          <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">R2 Storage Used</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-red-50 text-red-600">
            <Trash2 size={18} />
          </div>
          <div className="text-2xl font-black text-zinc-900">{overview.totalWipes}</div>
          <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">Wipes Executed</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-indigo-50 text-indigo-600">
            <FileText size={18} />
          </div>
          <div className="text-2xl font-black text-zinc-900">{totalDataItems.toLocaleString()}</div>
          <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">Total Data Items</div>
        </div>
      </div>

      {/* R2 Storage Breakdown */}
      {overview.storageBreakdown.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
          <h4 className="text-base font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <Activity size={16} className="text-emerald-600" /> R2 Storage Breakdown
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {overview.storageBreakdown.map(s => (
              <div key={s.folder} className="bg-zinc-50 border border-zinc-100 rounded-xl p-3">
                <div className="text-lg font-black text-zinc-900">{s.mb} <span className="text-xs text-zinc-400">MB</span></div>
                <div className="text-xs font-bold text-zinc-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Organisation Master Table */}
      <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <h4 className="text-base font-bold text-zinc-900">Organisation Data Health</h4>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search orgs…"
              className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none w-56" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 text-left">
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Organisation</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Plan</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Data Items</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Last Backup</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.map(org => {
                const totalItems = Object.values(org.moduleCounts).reduce((a, b) => a + b, 0);
                const moduleLabels = Object.entries(org.moduleCounts)
                  .map(([modId, count]) => {
                    const def = PLATFORM_MODULES.find(m => m.id === modId);
                    return `${def?.shortName ?? modId}: ${count}`;
                  })
                  .join(' · ');

                return (
                  <tr key={org.orgId} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-zinc-900">{org.orgName}</div>
                      <div className="text-[10px] text-zinc-400 font-mono">{org.orgId}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest',
                        org.plan === 'ENTERPRISE' ? 'bg-purple-100 text-purple-700' :
                        org.plan === 'PRO' ? 'bg-emerald-100 text-emerald-700' :
                        org.plan === 'BASIC' ? 'bg-blue-100 text-blue-700' :
                        'bg-zinc-100 text-zinc-500'
                      )}>
                        {org.plan}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-zinc-900">{totalItems.toLocaleString()}</div>
                      {moduleLabels && <div className="text-[10px] text-zinc-400 mt-0.5">{moduleLabels}</div>}
                    </td>
                    <td className="px-5 py-3">
                      {org.lastBackup ? (
                        <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                          <Clock size={11} /> {formatTimeAgo(org.lastBackup)}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-400 italic">Never</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => handleQuickInspect(org.orgId, org.orgName)}
                          title="Quick Inspect Data"
                          className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors">
                          <Eye size={14} />
                        </button>
                        <button onClick={() => { setPurgeModal({ orgId: org.orgId, orgName: org.orgName }); setPurgePassword(''); }}
                          title="Purge Operational Data"
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredOrgs.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400 italic">No organisations found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Purge Confirmation Modal */}
      {purgeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center gap-3 p-6 border-b border-zinc-100">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-zinc-900">☢️ Purge Operational Data</h2>
                <p className="text-sm text-zinc-500">{purgeModal.orgName}</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-zinc-700 leading-relaxed">
                This will <strong className="text-red-600">permanently delete ALL module data</strong> (riders, menus, campaigns, tickets, assets) for <strong>{purgeModal.orgName}</strong>. The organisation account itself will remain intact.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-700 font-semibold">🔐 Password Required — Enter YOUR admin password to authorize this cross-tenant wipe.</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Your Password</label>
                <input type="password" value={purgePassword} onChange={e => setPurgePassword(e.target.value)}
                  placeholder="Enter your admin password"
                  className="w-full px-4 py-3 text-sm border border-zinc-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-400 outline-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-100">
              <button onClick={() => { setPurgeModal(null); setPurgePassword(''); }}
                className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-800 transition-colors">
                Cancel
              </button>
              <button onClick={handlePurge} disabled={purging || !purgePassword}
                className="px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {purging ? 'Purging…' : '☢️ Purge All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const MATURITY_COLORS: Record<ModuleMaturity, string> = {
  STABLE:      'bg-emerald-100 text-emerald-700',
  BETA:        'bg-amber-100 text-amber-700',
  DEVELOPMENT: 'bg-slate-200 text-slate-600',
};

const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  Package, Globe, BookOpen, LifeBuoy, BarChart3, Boxes, Banknote,
};

export function LabPage() {
  const [selectedApp, setSelectedApp] = useState<AppDefinition | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Load organizations when a module is selected
  const openModuleDetail = useCallback(async (app: AppDefinition) => {
    setSelectedApp(app);
    setLoadingOrgs(true);
    const orgs = await repo.getOrganizations();
    setOrganizations(orgs);
    setLoadingOrgs(false);
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
          <LayoutGrid size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-black text-zinc-900">Module Catalog</h1>
          <p className="text-sm text-zinc-500">Platform features gallery — view all modules and their organization usage.</p>
        </div>
      </div>

      {/* Module Detail Panel (overlays gallery when a module is selected) */}
      {selectedApp ? (
        <div>
          {/* Back button */}
          <button
            onClick={() => setSelectedApp(null)}
            className="flex items-center gap-2 mb-6 text-sm font-bold text-zinc-500 hover:text-zinc-700 transition-colors"
          >
            <X size={14} /> Back to Catalog
          </button>

          {/* Module Header */}
          <div className="flex items-center gap-4 mb-8 p-6 bg-white border border-zinc-200 rounded-2xl shadow-sm">
            <div className={cn(
              "w-14 h-14 rounded-2xl flex items-center justify-center",
              selectedApp.status === 'live' ? "bg-indigo-50 text-indigo-600" : "bg-zinc-100 text-zinc-400"
            )}>
              {ICON_MAP[selectedApp.iconKey] && React.createElement(ICON_MAP[selectedApp.iconKey], { size: 26 })}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-black text-zinc-900">{selectedApp.name}</h2>
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest',
                  MATURITY_COLORS[selectedApp.maturity]
                )}>
                  {selectedApp.maturity}
                </span>
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest',
                  selectedApp.status === 'live' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-500'
                )}>
                  {selectedApp.status === 'live' ? 'Live' : 'Coming Soon'}
                </span>
              </div>
              <p className="text-sm text-zinc-500">{selectedApp.description}</p>
            </div>
          </div>

          {/* Organizations Using This App */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-4">
              Organizations Using This App ({organizations.filter(o => (o.enabledApps ?? []).includes(selectedApp.id)).length} of {organizations.length})
            </p>
            {loadingOrgs ? (
              <div className="text-center py-12 text-zinc-400 text-sm">Loading organizations…</div>
            ) : (
              <div className="border border-zinc-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-left px-4 py-3 font-bold text-zinc-600">Organization</th>
                      <th className="text-left px-4 py-3 font-bold text-zinc-600">Plan</th>
                      <th className="text-left px-4 py-3 font-bold text-zinc-600">Status</th>
                      <th className="text-center px-4 py-3 font-bold text-zinc-600">Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.map(org => {
                      const hasApp = (org.enabledApps ?? []).includes(selectedApp.id);
                      return (
                        <tr key={org.id} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-bold">
                                {org.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-bold text-zinc-900">{org.name}</p>
                                <p className="text-[11px] text-zinc-400">{org.slug}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn(
                              'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest',
                              PLAN_BADGE_COLORS[org.plan] ?? 'bg-zinc-100 text-zinc-600'
                            )}>
                              {PLAN_LABELS[org.plan] ?? org.plan}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn(
                              'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest',
                              org.status === OrgStatus.ACTIVE ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-500'
                            )}>
                              {org.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={cn(
                              'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest',
                              hasApp ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400'
                            )}>
                              {hasApp ? 'Enabled' : 'Not Enabled'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Module Gallery Grid */
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-4">Platform Features ({APP_REGISTRY.length})</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {APP_REGISTRY.map(app => {
              const IconComp = ICON_MAP[app.iconKey];
              const hasRoute = app.status === 'live';
              return (
                <button
                  key={app.id}
                  onClick={() => openModuleDetail(app)}
                  className="text-left bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      hasRoute ? "bg-indigo-50 text-indigo-600" : "bg-zinc-100 text-zinc-400"
                    )}>
                      {IconComp && <IconComp size={20} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-zinc-900 text-sm group-hover:text-indigo-700 transition-colors">{app.name}</p>
                        <span className={cn(
                          'px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest',
                          MATURITY_COLORS[app.maturity]
                        )}>
                          {app.maturity}
                        </span>
                      </div>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">{app.description}</p>
                    </div>
                  </div>
                  {hasRoute ? (
                    <div className="flex items-center gap-2 text-[11px] font-bold text-indigo-500">
                      <Eye size={12} /> View Organizations
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-[11px] font-bold text-zinc-400">
                      <Lock size={12} /> Coming Soon — Under Development
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main SaasAdminPage component
// ─────────────────────────────────────────────────────────────────────────────

type TabId = 'organizations' | 'approvals' | 'requests' | 'health' | 'subscriptions' | 'maintenance';

const TABS: { id: TabId; label: string; icon: React.FC<{ size?: number }> }[] = [
  { id: 'organizations', label: 'Organisations',   icon: Building2 },
  { id: 'approvals',     label: 'Approvals',       icon: CheckCircle },
  { id: 'requests',      label: 'Module Requests', icon: Inbox },
  { id: 'health',        label: 'Platform Health', icon: BarChart3 },
  { id: 'subscriptions', label: 'Subscriptions',   icon: CreditCard },
  { id: 'maintenance',   label: 'Maintenance',     icon: HardDrive },
];

export function SaasAdminPage({
  onSwitchOrgContext,
  currentUser,
  onImpersonate,
  onInspect,
}: {
  onSwitchOrgContext: (orgId: string | null) => Promise<void>;
  currentUser?: { id?: string; name?: string; role?: UserRole; canInspectTenants?: boolean } | null;
  onImpersonate?: (org: Organization) => void;
  onInspect?: (org: Organization) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Platform role gating ──
  const userRole = currentUser?.role;
  const isOwner = isOwnerRole(userRole);
  const isSupport = userRole === UserRole.PLATFORM_SUPPORT;
  const isSales = userRole === UserRole.PLATFORM_SALES;

  // Tab-level access: health + subscriptions = OWNER only
  const tabAccess: Record<TabId, boolean> = {
    organizations: true,
    approvals: isOwner,
    requests: true,
    health: isOwner,
    subscriptions: isOwner,
    maintenance: isOwner,
  };

  // Derive tab from URL: /saas-admin/health → 'health', /saas-admin/requests → 'requests', etc.
  const tabFromUrl = (): TabId => {
    const segment = location.pathname.split('/').filter(Boolean).pop() || '';
    const validTabs: TabId[] = ['organizations', 'approvals', 'requests', 'health', 'subscriptions', 'maintenance'];
    return validTabs.includes(segment as TabId) ? (segment as TabId) : 'organizations';
  };

  const [activeTab, setActiveTabState] = useState<TabId>(tabFromUrl);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Sync tab when URL changes (e.g. sidebar navigation)
  useEffect(() => {
    const resolved = tabFromUrl();
    // Hard redirect if user doesn't have access to this tab
    if (!tabAccess[resolved]) {
      navigate('/saas-admin/organizations', { replace: true });
      return;
    }
    setActiveTabState(resolved);
  }, [location.pathname]);

  // Navigate when tab is clicked from the internal tab bar
  const setActiveTab = (tab: TabId) => {
    navigate(`/saas-admin/${tab}`, { replace: true });
  };

  // Inspect gating — defaults to true if not explicitly set to false.  SALES never inspects.
  const canInspect = !isSales && currentUser?.canInspectTenants !== false;

  // Delete / plan change gating — OWNER only
  const canDeleteOrg = isOwner;
  const canChangePlan = isOwner;

  // Load pending request count for tab badge
  useEffect(() => {
    repo.getModuleRequests().then(reqs => {
      setPendingRequestCount(reqs.filter(r => r.status === ModuleRequestStatus.PENDING).length);
    });
    repo.getOrganizations().then(orgs => {
      setPendingApprovalCount(orgs.filter(org => org.status === OrgStatus.PENDING_APPROVAL || org.status === OrgStatus.PENDING_PAYMENT).length);
    });
  }, [activeTab]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
          <ShieldCheck size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-black text-zinc-900">Platform Admin</h1>
          <p className="text-sm text-zinc-500">
            {isOwner ? 'Full platform control — manage organisations, plans, and platform health.'
             : isSupport ? 'Support view — manage organisations and review module requests.'
             : 'Sales view — manage organisations and module catalog.'}
          </p>
        </div>
      </div>

      {/* Tabs — only show tabs the user can access */}
      <div className="flex gap-1 bg-zinc-100 rounded-xl p-1 w-fit mb-8">
        {TABS.filter(t => tabAccess[t.id]).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all',
              activeTab === id
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            )}
          >
            <Icon size={14} /> {label}
            {id === 'approvals' && pendingApprovalCount > 0 && (
              <span className="ml-1 w-5 h-5 bg-amber-500 text-white text-[10px] font-black flex items-center justify-center rounded-full">
                {pendingApprovalCount}
              </span>
            )}
            {id === 'requests' && pendingRequestCount > 0 && (
              <span className="ml-1 w-5 h-5 bg-amber-500 text-white text-[10px] font-black flex items-center justify-center rounded-full">
                {pendingRequestCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'organizations'  && <OrganizationsTab onImpersonate={onImpersonate ?? (() => {})} onInspect={onInspect ?? (() => {})} canInspect={canInspect} canDeleteOrg={canDeleteOrg} canChangePlan={canChangePlan} />}
      {activeTab === 'approvals'      && <OrgApprovalsTab currentUser={currentUser} />}
      {activeTab === 'requests'       && <ModuleRequestsTab currentUser={currentUser} />}
      {activeTab === 'health'         && <PlatformHealthTab userRole={currentUser?.role} />}
      {activeTab === 'subscriptions'  && <SubscriptionsTab />}
      {activeTab === 'maintenance'    && <PlatformMaintenanceTab onInspect={onInspect ?? (() => {})} />}
    </div>
  );
}
